<template>
  <div
    v-if="open || embedded"
    class="sp-overlay"
    :class="{
      'sp-instant': instantOpen,
      'sp-departing': viewDeparting,
      'sp-embedded': embedded,
      'sp-content-fit': embedded && fitContent,
      'sp-mobile-closing': mobileClosing,
    }"
    @pointerdown="onOverlayPointerDown"
    @pointerup="onOverlayPointerUp"
    @click.self="onOverlayClick"
  >
    <div
      ref="panel"
      class="sp-panel"
      :class="{ 'sp-panel-map': mode === 'map' }"
      role="dialog"
      :aria-modal="(!embedded).toString()"
      :aria-label="paletteLabel"
    >
      <div class="sp-input-row" :inert="embeddedDemoLocked">
        <i class="sp-input-icon" :class="modeIcon" aria-hidden="true"></i>
        <input
          ref="input"
          v-model="query"
          class="sp-input"
          type="text"
          :placeholder="inputPlaceholder"
          autocomplete="off"
          autocorrect="off"
          spellcheck="false"
          :aria-label="inputLabel"
          :disabled="askInputLocked"
          :aria-busy="(mode === 'ask' && askBusy).toString()"
          @compositionstart="composing = true"
          @compositionend="composing = false"
          @keydown="onInputKeydown($event)"
          @keydown.down="onArrow($event, 1)"
          @keydown.up="onArrow($event, -1)"
          @keydown.enter="onEnter($event)"
        />
        <button
          v-if="query"
          type="button"
          class="sp-clear"
          aria-label="Clear query"
          :disabled="askInputLocked"
          @click="clearQuery"
        >
          <i class="fas fa-times-circle" aria-hidden="true"></i>
        </button>
        <button v-if="!embedded" type="button" class="sp-esc" aria-label="Close palette" @click="close">
          <span class="sp-close-desktop">esc</span>
          <MobileMenuGlyph class="sp-close-mobile" :open="!mobileClosing" />
        </button>
      </div>

      <div v-if="!embedded" class="sp-modes">
        <button
          type="button"
          class="sp-mobile-mode-trigger"
          :class="{ open: mobileModeMenuOpen }"
          :aria-expanded="mobileModeMenuOpen.toString()"
          aria-haspopup="menu"
          :disabled="viewBusy"
          @click="mobileModeMenuOpen = !mobileModeMenuOpen"
        >
          <span class="sp-mobile-mode-current">
            <AiAskTerm v-if="mode === 'ask'" />
            <AiViewTerm v-else-if="mode === 'view'" />
            <span v-else>{{ mode === 'map' ? 'Map' : 'Search' }}</span>
          </span>
          <i class="fas fa-chevron-down" aria-hidden="true"></i>
        </button>
        <div class="sp-mode-segments" role="group" aria-label="Palette mode">
          <button
            type="button"
            class="sp-mode"
            :class="{ active: mode === 'ask' }"
            :aria-pressed="(mode === 'ask').toString()"
            :disabled="viewBusy"
            @click="setMode('ask')"
          ><AiAskTerm /></button>
          <button
            type="button"
            class="sp-mode"
            :class="{ active: mode === 'view' }"
            :aria-pressed="(mode === 'view').toString()"
            :disabled="viewBusy"
            @click="setMode('view')"
          ><AiViewTerm /></button>
          <button
            type="button"
            class="sp-mode"
            :class="{ active: mode === 'search' }"
            :aria-pressed="(mode === 'search').toString()"
            :disabled="viewBusy"
            @click="setMode('search')"
          >Search</button>
          <button
            type="button"
            class="sp-mode"
            :class="{ active: mode === 'map' }"
            :aria-pressed="(mode === 'map').toString()"
            :disabled="viewBusy"
            @click="setMode('map')"
          >Map</button>
        </div>
        <transition name="sp-mobile-mode-menu">
          <div v-if="mobileModeMenuOpen" class="sp-mobile-mode-menu" role="menu" aria-label="Choose how to explore">
            <button type="button" class="sp-mobile-mode-option" :class="{ active: mode === 'ask' }" role="menuitem" :disabled="viewBusy" @click="selectMobileMode('ask')">
              <i class="far fa-comment-dots" aria-hidden="true"></i>
              <span><strong><AiAskTerm /></strong><small>Answer with sources</small></span>
              <span class="sp-mobile-mode-dot" aria-hidden="true"></span>
            </button>
            <button type="button" class="sp-mobile-mode-option" :class="{ active: mode === 'view' }" role="menuitem" :disabled="viewBusy" @click="selectMobileMode('view')">
              <i class="fas fa-cube" aria-hidden="true"></i>
              <span><strong><AiViewTerm /></strong><small>Compose a reading path</small></span>
              <span class="sp-mobile-mode-dot" aria-hidden="true"></span>
            </button>
            <button type="button" class="sp-mobile-mode-option" :class="{ active: mode === 'search' }" role="menuitem" :disabled="viewBusy" @click="selectMobileMode('search')">
              <i class="fas fa-search" aria-hidden="true"></i>
              <span><strong>Search</strong><small>Find original material</small></span>
              <span class="sp-mobile-mode-dot" aria-hidden="true"></span>
            </button>
            <button type="button" class="sp-mobile-mode-option" :class="{ active: mode === 'map' }" role="menuitem" :disabled="viewBusy" @click="selectMobileMode('map')">
              <i class="far fa-map" aria-hidden="true"></i>
              <span><strong>Map</strong><small>Explore ideas spatially</small></span>
              <span class="sp-mobile-mode-dot" aria-hidden="true"></span>
            </button>
          </div>
        </transition>
        <span v-if="semStatus" class="sp-mode-status">{{ semStatus }}</span>
        <div v-if="mode === 'map'" class="sp-projection-toggle" aria-label="Map projection">
          <button
            type="button"
            class="sp-projection"
            :class="{ active: mapProjection === '2d' }"
            :aria-pressed="(mapProjection === '2d').toString()"
            @click="setMapProjection('2d')"
          >2D</button>
          <button
            type="button"
            class="sp-projection"
            :class="{ active: mapProjection === '3d' }"
            :aria-pressed="(mapProjection === '3d').toString()"
            @click="setMapProjection('3d')"
          >3D</button>
        </div>
      </div>

      <div ref="results" class="sp-results" :inert="embeddedDemoLocked" @pointerdown="mobileModeMenuOpen = false">
        <div v-if="mode === 'map'" class="sp-map">
          <EmbeddingMap
            ref="paletteMap"
            compact
            fit-stage
            :initial-view-mode="mapProjection"
            :show-controls="false"
            :show-view-toggle="false"
            :show-caption="false"
            :show-hits="false"
            :lock-pan-on-touch="false"
            @navigate="close"
          />
        </div>

        <template v-else-if="mode === 'search'">
          <template v-if="query.trim() && groups.length">
            <div v-for="g in groups" :key="g.page" class="sp-group">
              <div class="sp-group-label">{{ g.page }}</div>
              <button
                v-for="r in g.items"
                :key="r.flatIndex"
                class="sp-result"
                :class="{ active: r.flatIndex === activeIndex }"
                :ref="el => setRowRef(el, r.flatIndex)"
                @mousemove="activeIndex = r.flatIndex"
                @click="openResult(r, 'click')"
              >
                <span class="sp-result-title" v-html="r.titleHtml"></span>
                <span v-if="r.snippetHtml" class="sp-result-snippet" v-html="r.snippetHtml"></span>
              </button>
            </div>
          </template>

          <div v-else-if="query.trim() && semBusy" class="sp-empty">
            {{ semLoading ? 'Getting search ready…' : 'Searching…' }}
          </div>

          <div v-else-if="query.trim()" class="sp-empty">
            No matches for &ldquo;{{ query.trim() }}&rdquo;.
          </div>

          <div v-else class="sp-group">
            <div class="sp-group-label">Jump to</div>
            <button
              v-for="s in suggestions"
              :key="s.route"
              class="sp-result sp-jump"
              @click="go(s.route, '')"
            >
              <span class="sp-result-title">{{ s.label }}</span>
              <span class="sp-jump-arrow" aria-hidden="true">&rarr;</span>
            </button>
          </div>
        </template>

        <div v-else-if="mode === 'view'" class="sp-view-compose">
          <div v-if="!askReady" class="sp-empty">
            View is temporarily unavailable. Please try again later.
          </div>
          <ViewComposeProgress v-else-if="viewBusy" :started-at="viewStartedAt" />
          <ViewComposeProgress
            v-else-if="externalComposeActive"
            :started-at="externalComposeStartedAt"
          />
          <div v-else-if="viewError" class="sp-empty sp-view-error" role="alert">{{ viewError }}</div>
          <div v-else class="sp-view-start">
            <p class="sp-view-note"><AiViewTerm /> connects material from across Henry&rsquo;s site around your question. Read it as an article or explore it as an exhibition.</p>
            <div class="sp-view-heading-row">
              <span class="sp-view-examples-label">Recently shared</span>
              <button type="button" class="sp-view-public-link" @click="openPublicViews">
                <span>Explore more</span>
                <ArrowUpRightIcon />
              </button>
            </div>
            <div v-if="recentSharedViews.length" class="sp-view-examples" aria-label="Recently shared Views">
              <button
                v-for="view in recentSharedViews"
                :key="view.id"
                type="button"
                @click="openSharedView(view.id)"
              >
                <span class="sp-view-example-copy">
                  <strong>{{ view.title }}</strong>
                  <small>&ldquo;{{ view.question }}&rdquo;</small>
                </span>
                <span class="sp-view-example-arrow" aria-hidden="true">→</span>
              </button>
            </div>
            <div
              v-else-if="recentSharedViewsLoading"
              class="sp-view-examples sp-view-examples-loading"
              role="status"
              aria-label="Loading recently shared Views"
            >
              <span v-for="index in 3" :key="index" class="sp-view-skeleton-row" aria-hidden="true">
                <span class="sp-view-skeleton-copy">
                  <span class="sp-view-skeleton-line sp-view-skeleton-title"></span>
                  <span class="sp-view-skeleton-line sp-view-skeleton-question"></span>
                </span>
                <span class="sp-view-skeleton-mark"></span>
              </span>
            </div>
          </div>
        </div>

        <div v-else class="sp-ask">
          <div v-if="!askReady" class="sp-empty">
            Ask is temporarily unavailable. Please try again later.
          </div>
          <template v-else>
            <ViewComposeProgress v-if="viewBusy" :started-at="viewStartedAt" />
            <template v-else-if="answerText">
              <transition
                appear
                @before-enter="beforeAnswerEnter"
                @enter="enterAnswer"
                @leave="leaveAnswer"
              >
                <p class="sp-ask-answer" v-html="answerHtml"></p>
              </transition>
              <AskAnswerAudio v-if="!askBusy" :text="answerText" />
              <AskActions
                v-if="askActionsList.length && answerText && !askBusy && !viewBusy"
                :actions="askActionsList"
                surface="palette"
              />
            </template>
            <TextShimmerWave
              v-else-if="askBusy"
              :children="askStatus || 'Thinking…'"
              as="div"
              class-name="sp-empty sp-thinking"
            />
            <div v-else-if="viewError" class="sp-empty sp-view-error" role="alert">{{ viewError }}</div>
            <div v-else-if="askError" class="sp-empty" role="alert">{{ askErrorText }}</div>
            <div v-else class="sp-empty sp-ask-empty">
              <button
                type="button"
                class="sp-ask-sample"
                :class="{ visible: sampleVisible }"
                @click="useCurrentSample"
              >{{ currentSamplePrompt }}</button>
            </div>

            <AskViewOffer
              v-if="(viewOfferVisible || canReturnToView) && answerText && !askBusy && !askError && !viewBusy"
              :ready="canReturnToView"
              :title="viewOffer.title"
              :description="viewOffer.description"
              @explore="buildViewFromCurrent"
            />

            <div v-if="askFollowUpQuestions.length && answerText && !askBusy && !viewBusy" class="sp-ask-followups">
              <div class="sp-group-label">Keep exploring</div>
              <div class="sp-ask-followup-list">
                <button
                  v-for="q in askFollowUpQuestions"
                  :key="q"
                  type="button"
                  class="sp-ask-followup ask-followup-chip"
                  @click="askFollowUp(q)"
                >{{ q }}</button>
              </div>
            </div>

            <div v-if="citedSources.length && !askBusy && !viewBusy" class="sp-ask-sources">
              <div class="sp-group-label">Sources</div>
              <transition-group
                appear
                tag="div"
                class="sp-ask-source-list"
                @before-enter="beforeChipEnter"
                @enter="enterChip"
                @leave="leaveChip"
              >
                <button
                  v-for="s in citedSources"
                  :key="`${s.route}#${s.anchor || ''}`"
                  :data-source-key="`${s.route}#${s.anchor || ''}`"
                  class="sp-ask-source ask-source-link"
                  :class="{ 'is-previewed': hoveredSourceId === `${s.route}#${s.anchor || ''}` }"
                  @mouseenter="hoveredSourceId = `${s.route}#${s.anchor || ''}`"
                  @mouseleave="hoveredSourceId = null"
                  @focus="hoveredSourceId = `${s.route}#${s.anchor || ''}`"
                  @blur="hoveredSourceId = null"
                  @click="openAskSource(s)"
                >{{ s.page }} › {{ s.displayTitle }}</button>
              </transition-group>
            </div>
          </template>
        </div>
      </div>

      <DemoInteractionGate
        v-if="embeddedDemoEnabled"
        :active="embeddedDemoActive"
        :label="embeddedDemoLabel"
        @activate="activateEmbeddedDemo"
        @deactivate="deactivateEmbeddedDemo"
      />

      <!-- Hover preview floats above the panel content (and never pushes the
           layout): it is positioned against the panel and follows the hovered
           source chip, recomputed on results scroll. -->
      <transition name="sp-preview-fade">
        <div
          v-if="askSourcePreview"
          :key="askSourcePreview.id"
          class="sp-ask-preview"
          :style="previewStyle"
        >
          <span class="sp-ask-preview-kicker">{{ askSourcePreview.page }} › {{ askSourcePreview.title }}</span>
          <p class="sp-ask-preview-text">{{ askSourcePreview.snippet }}</p>
        </div>
      </transition>

      <div v-if="!embedded" class="sp-foot">
        <span><kbd>⌥</kbd> mode</span>
        <span><kbd>&crarr;</kbd> {{ mode === 'ask' ? 'ask' : (mode === 'view' ? 'compose' : (mode === 'map' ? 'place' : 'open')) }}</span>
        <span v-if="mode === 'search'"><kbd>&uarr;</kbd><kbd>&darr;</kbd> move</span>
        <span><kbd>esc</kbd> close</span>
      </div>
    </div>
  </div>
</template>

<script>
import { lockBodyScroll } from '../utils/bodyScrollLock';
import AskViewOffer from './ui/AskViewOffer.vue';
import { prepareAskSourceReturn, consumeAskSourceDeparture } from '../utils/askReturn.js';
import Fuse from 'fuse.js';
import EmbeddingMap from './EmbeddingMap.vue';
import ViewComposeProgress from './view/ViewComposeProgress.vue';
import TextShimmerWave from './ui/TextShimmerWave.vue';
import AskAnswerAudio from './ui/AskAnswerAudio.vue';
import AskActions from './ui/AskActions.vue';
import AiViewTerm from './ui/AiViewTerm.vue';
import AiAskTerm from './ui/AiAskTerm.vue';
import MobileMenuGlyph from './ui/MobileMenuGlyph.vue';
import DemoInteractionGate from './ui/DemoInteractionGate.vue';
import { animate } from 'motion';
import { ensureReady, semanticRank } from '../utils/semanticSearch';
import { askConfigured, askStream, askErrorMessage } from '../utils/askSite';
import { ASK_SAMPLE_PROMPTS } from '../utils/askPrompts';
import {
  allAskContexts,
  diversifyAskWritingContexts,
  askAnswerFollowUps,
  askResponseIsComplete,
  citedSourcesFromAnswer,
  cleanAskAnswer,
  cleanAskContextText,
  groundAskFollowUps,
  looksLikeViewBuildRequest,
  sourceDisplayTitle,
  takeRetrievalTrace,
  askViewRouting,
  askViewOffer,
} from '../utils/askRetrieval';
import {
  loadAskSession,
  addAskTurn,
  setAskView,
  setAskOrigin,
  recentAskTurns,
  askSourceIdsInRecent,
  recentDuplicateViewQuestion,
  matchingAskView,
} from '../utils/askSession';
import {
  beginViewCompose,
  cleanViewIntent,
  developViewDelay,
  DEVELOP_VIEW_LABEL,
  DEVELOP_VIEW_ROUTE,
  endViewCompose,
  isDevelopMode,
  isViewComposing,
  requestView,
  viewComposeState,
} from '../utils/viewSite';
import { candidatesForView } from '../utils/viewRetrieval';
import { listPublicViews } from '../utils/viewShareSite';
import { sanitizeViewResponse, validateViewResponse } from '../utils/viewValidation';
import { askActionsForDisplay, renderAskAnswerLinks } from '../utils/askActions';
import { settleScrollToId } from '../utils/anchorLanding';
import { sourceRouteTarget, projectWebsiteTarget } from '../utils/viewSourceTarget';
import {
  logAskClient,
  logEvent,
  logSearch,
  logSearchClick,
  newAskContext,
  newId,
  sessionId,
} from '../utils/askTelemetry';

function collectViewSourceIds(view) {
  const ids = [];
  for (const section of (view && view.sections) || []) {
    for (const block of (section && section.blocks) || []) {
      if (block && block.source_id) ids.push(block.source_id);
      if (block && block.source_ids) ids.push(...block.source_ids);
    }
  }
  return [...new Set(ids)];
}

function isGeneratedViewPath(path) {
  const value = String(path || '');
  return value === '/view' || value.startsWith('/view/');
}

const STOP = new Set([
  'the', 'a', 'an', 'of', 'on', 'in', 'to', 'is', 'are', 'was', 'were', 'be',
  'and', 'or', 'for', 'with', 'at', 'by', 'from', 'as', 'it', 'i', 'my', 'me',
  'this', 'that', 'how', 'what', 'we', 'you', 'they', 'but', 'so', 'if', 'into',
  'about', 'out', 'up', 'over', 'than', 'then', 'has', 'have', 'had', 'do',
]);


export default {
  name: 'SearchPalette',
  emits: ['ai-complete'],
  components: { AskViewOffer, AiAskTerm, AiViewTerm, AskActions, AskAnswerAudio, DemoInteractionGate, EmbeddingMap, MobileMenuGlyph, TextShimmerWave, ViewComposeProgress },
  props: {
    // Rendered inline (no overlay, no scroll lock, no global shortcuts) with
    // a fixed mode and optional prefilled state. Used by the SearchStory page
    // to embed the same search / ask experience the palette provides.
    embedded: {
      type: Boolean,
      default: false,
    },
    fitContent: {
      type: Boolean,
      default: false,
    },
    fixedMode: {
      type: String,
      default: '',
    },
    initialQuery: {
      type: String,
      default: '',
    },
    initialAnswer: {
      type: String,
      default: '',
    },
    initialSources: {
      type: Array,
      default: () => [],
    },
    initialFollowUps: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      open: false,
      instantOpen: false,
      query: '',
      savedQueries: { search: '', ask: '', view: '', map: '' },
      mapProjection: '3d',
      items: [],
      fuse: null,
      loaded: false,
      activeIndex: 0,
      rowRefs: {},
      composing: false,
      mode: 'ask',
      phoneLayout: typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches,
      semHits: [],
      semLoading: false,
      semQuerying: false,
      semError: '',
      askAnswer: '',
      askSources: [],
      askBusy: false,
      askError: '',
      askStatus: '',
      viewRouting: 'none',
      viewOfferVisible: false,
      viewBusy: false,
      viewStartedAt: 0,
      externalViewJobId: '',
      viewDeparting: false,
      viewError: '',
      recentSharedViews: [],
      recentSharedViewsLoading: false,
      mobileModeMenuOpen: false,
      mobileClosing: false,
      viewSession: null,
      hoveredSourceId: null,
      previewStyle: null,
      groundedFollowUps: null,
      overlayPointerStartedOutside: false,
      overlayPointerEndedOutside: false,
      sampleVisible: true,
      sampleIndex: Math.floor(Math.random() * ASK_SAMPLE_PROMPTS.length),
      samplePrompts: ASK_SAMPLE_PROMPTS,
      embeddedDemoActive: false,
      suggestions: [
        { label: 'About', route: '/about' },
        { label: 'Study', route: '/education' },
        { label: 'Work', route: '/experience' },
        { label: 'Research', route: '/research' },
        { label: 'Builds', route: '/projects' },
        { label: 'Fragments', route: '/fragments' },
        { label: 'Views', route: '/views' },
        { label: 'CV', route: '/cv' },
      ],
    };
  },
  computed: {
    externalComposeActive() {
      return isViewComposing() && !this.viewBusy;
    },
    externalComposeStartedAt() {
      return viewComposeState.startedAt;
    },
    askInputLocked() {
      return this.viewBusy
        || this.externalComposeActive
        || (this.mode === 'ask' && this.askBusy);
    },
    askErrorText() {
      return askErrorMessage();
    },
    itemsById() {
      const m = {};
      for (const it of this.items) m[it.id] = it;
      return m;
    },
    semBusy() {
      return this.semLoading || this.semQuerying;
    },
    semStatus() {
      if (this.mode === 'map') return '';
      if (this.semError) return 'limited search';
      if (this.semLoading) return 'getting ready…';
      return '';
    },
    paletteLabel() {
      if (this.mode === 'ask') return 'Ask this site';
      if (this.mode === 'view') return 'Compose a View';
      if (this.mode === 'map') return 'Map the site';
      return 'Search the site';
    },
    modeIcon() {
      return { ask: 'far fa-comment-dots', view: 'fas fa-cube', search: 'fa fa-search', map: 'far fa-map' }[this.mode] || 'fa fa-search';
    },
    inputLabel() {
      if (this.mode === 'ask') return 'Ask question';
      if (this.mode === 'view') return 'Describe the View you want';
      if (this.mode === 'map') return 'Map query';
      return 'Search query';
    },
    inputPlaceholder() {
      if (this.phoneLayout) {
        return {
          ask: 'Ask about Henry...',
          view: 'A question or theme...',
          map: 'Map a question...',
          search: 'Search the site...',
        }[this.mode];
      }
      if (this.mode === 'ask') return 'Ask anything about Henry...';
      if (this.mode === 'view') return 'I want to understand Henry through...';
      if (this.mode === 'map') return 'Drop a question into the map...';
      return 'Search the site...';
    },
    askReady() {
      return askConfigured();
    },
    answerText() {
      return cleanAskAnswer(this.askAnswer);
    },
    answerHtml() {
      return renderAskAnswerLinks(this.answerText);
    },
    askActionsList() {
      return askActionsForDisplay(this.askAnswer, this.viewRouting);
    },
    viewOffer() {
      return askViewOffer(this.askAnswer);
    },
    canReturnToView() {
      return !isDevelopMode(this.query)
        && !!matchingAskView(cleanViewIntent(this.query), this.viewSession);
    },
    citedSources() {
      const seen = new Set();
      const out = [];
      const base = citedSourcesFromAnswer(this.askAnswer, this.askSources);
      for (const source of base) {
        const key = `${source.route}#${source.anchor || ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          ...source,
          displayTitle: this.askDisplayTitle(source),
        });
      }
      return out;
    },
    // Hovering a cited source peeks at its passage without leaving the panel.
    askSourcePreview() {
      if (!this.hoveredSourceId) return null;
      const s = this.citedSources.find(
        (x) => `${x.route}#${x.anchor || ''}` === this.hoveredSourceId,
      );
      if (!s) return null;
      const text = cleanAskContextText(s.body || '').replace(/\s+/g, ' ').trim();
      const snippet = text
        ? (text.length > 220 ? `${text.slice(0, 220).trim()}…` : text)
        : 'Open the passage to read it.';
      return { id: this.hoveredSourceId, page: s.page, title: s.displayTitle, snippet };
    },
    // Only show questions written for this answer and grounded in the site.
    // An empty grounding result intentionally leaves the section hidden.
    askFollowUpQuestions() {
      if (this.askBusy || !this.answerText) return [];
      const rawAiFollowUps = askAnswerFollowUps(this.askAnswer);
      if (this.groundedFollowUps !== null) {
        return this.groundedFollowUps;
      }
      return rawAiFollowUps;
    },
    currentSamplePrompt() {
      return this.samplePrompts[this.sampleIndex] || '';
    },
    embeddedDemoEnabled() {
      return this.embedded && ['search', 'ask'].includes(this.mode);
    },
    embeddedDemoLocked() {
      return this.embeddedDemoEnabled && !this.embeddedDemoActive;
    },
    embeddedDemoLabel() {
      return this.mode === 'ask' ? 'Try Ask' : 'Try Search';
    },
    results() {
      if (this.mode === 'ask' || this.mode === 'view') return [];
      const q = this.query.trim();
      if (!q) return [];
      const seen = new Set();
      const out = [];
      for (const id of this.hybridIds(q)) {
        const item = this.itemsById[id];
        if (!item) continue;
        const key = `${item.route}#${item.anchor || ''}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(item);
        if (out.length >= 12) break;
      }
      return out;
    },
    groups() {
      const out = [];
      const byPage = new Map();
      for (const item of this.results) {
        const body = this.searchDisplayBody(item.body);
        const titleHtml = this.highlight(this.displayTitle(item));
        const snippetHtml = this.snippet(body) || this.leadSnippet(body);
        const row = { ...item, titleHtml, snippetHtml };
        if (!byPage.has(item.page)) {
          const g = { page: item.page, items: [] };
          byPage.set(item.page, g);
          out.push(g);
        }
        byPage.get(item.page).items.push(row);
      }
      let fi = 0;
      for (const group of out) {
        for (const item of group.items) item.flatIndex = fi++;
      }
      return out;
    },
    flatList() {
      return this.groups.flatMap(g => g.items);
    },
  },
  watch: {
    '$route.fullPath'(path, previousPath) {
      const snapshot = this._viewReturnSnapshot;
      if (!snapshot || !isGeneratedViewPath(previousPath)) return;
      if (path === snapshot.route) {
        this.restorePaletteAfterView(snapshot);
      } else if (!isGeneratedViewPath(path)) {
        this._viewReturnSnapshot = null;
      }
    },
    query() {
      this.activeIndex = 0;
      if (this.mode === 'search' && this.query.trim()) {
        this.warmSemantic();
        this.scheduleSemantic();
        this.scheduleSearchLog();
      }
    },
    semHits() {
      if (this.mode === 'search' && this.query.trim()) this.scheduleSearchLog();
    },
    hoveredSourceId() {
      // The chip just gained/lost hover: position the floating preview against
      // the chip once the DOM has settled, or clear it.
      this.$nextTick(this.updateAskPreviewPosition);
    },
  },
  mounted() {
    this._onAskSessionUpdated = event => { this.viewSession = event.detail; };
    window.addEventListener('ask-session-updated', this._onAskSessionUpdated);
    this.viewSession = loadAskSession();
    this._semSeq = 0;
    this._askSeq = 0;
    this._viewSeq = 0;
    this._viewAbortController = null;
    this._openedAt = 0;
    this._searchId = null;
    this._searchAt = 0;
    this._loggedSearchQuery = '';
    this._lastAskEventId = null;
    this._spAnims = new Set();
    this._viewReturnSnapshot = null;
    this._viewRestoreTimers = [];

    if (this.embedded) {
      // Inline mode: stay open, pin the requested mode, and prefill search or
      // ask state so the embedded panel opens with real content instead of an
      // empty prompt.
      this.open = true;
      if (this.fixedMode && ['search', 'ask', 'view', 'map'].includes(this.fixedMode)) {
        this.mode = this.fixedMode;
      }
      if (this.initialQuery) this.query = this.initialQuery;
      if (this.initialAnswer) this.askAnswer = this.initialAnswer;
      if (this.initialSources && this.initialSources.length) this.askSources = this.initialSources;
      if (this.initialFollowUps && this.initialFollowUps.length) this.groundedFollowUps = this.initialFollowUps;
      this.loadIndex();
      this.warmSemantic();
      if (this.mode === 'search' && this.query.trim()) {
        this.semLoading = true;
        this.runSemantic();
      }
    } else {
      this._sampleTimer = window.setInterval(this.rotateSample, 3200);
      window.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this._onAskEntryClick);
    }

    // Any scroll gesture invalidates the hover anchor: drop the preview at
    // once instead of letting it float at a stale position. Capture phase
    // catches scrolls from the results container too (scroll does not bubble).
    this._onAnyScroll = () => {
      if (this._viewReturnSnapshot) return;
      if (this.hoveredSourceId) this.hoveredSourceId = null;
    };
    window.addEventListener('scroll', this._onAnyScroll, { capture: true, passive: true });
    this._onWindowResize = () => {
      this.phoneLayout = window.matchMedia('(max-width: 760px)').matches;
      if (this.hoveredSourceId) this.updateAskPreviewPosition();
    };
    window.addEventListener('resize', this._onWindowResize, { passive: true });
  },
  unmounted() {
    window.removeEventListener('ask-session-updated', this._onAskSessionUpdated);
    if (this._viewAbortController) this._viewAbortController.abort();
    if (this._spAnims) {
      this._spAnims.forEach((animation) => animation.stop());
      this._spAnims.clear();
    }
    if (!this.embedded) {
      window.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this._onAskEntryClick);
      window.clearInterval(this._sampleTimer);
      window.clearTimeout(this._sampleFadeTimer);
      this.unlockScroll();
    }
    window.removeEventListener('scroll', this._onAnyScroll, { capture: true, passive: true });
    window.removeEventListener('resize', this._onWindowResize, { passive: true });
    window.clearTimeout(this._searchLogTimer);
    window.clearTimeout(this._mobileCloseTimer);
    this._viewRestoreTimers.forEach((timer) => window.clearTimeout(timer));
    this.unlockScroll();
  },
  methods: {
    activateEmbeddedDemo() {
      this.embeddedDemoActive = true;
      this.$nextTick(() => this.$refs.input?.focus({ preventScroll: true }));
    },
    deactivateEmbeddedDemo() {
      this.embeddedDemoActive = false;
      this.$nextTick(() => this.$refs.panel?.querySelector('.demo-interaction-enter')?.focus({ preventScroll: true }));
    },
    // Entry points rendered inside Ask answers navigate to another page.
    // Closing here keeps the palette from floating over the destination.
    _onAskEntryClick(e) {
      if (!this.open) return;
      const target = e.target;
      if (!target || !target.closest) return;
      if (target.closest('.ask-inline-link') || target.closest('.ask-action[data-ask-navigate]')) {
        this.rememberSourceReturn();
        this.finishClose();
      }
    },
    // The hover preview floats above the panel: anchor it to the hovered chip
    // in panel coordinates, flip above when there is no room below, and never
    // participate in layout.
    updateAskPreviewPosition() {
      if (!this.hoveredSourceId) {
        this.previewStyle = null;
        return;
      }
      const panel = this.$refs.panel;
      const chip = panel && panel.querySelector(`.sp-ask-source[data-source-key="${this.hoveredSourceId}"]`);
      if (!panel || !chip) return;
      const pr = panel.getBoundingClientRect();
      const cr = chip.getBoundingClientRect();
      const gap = 8;
      const left = Math.max(12, cr.left - pr.left);
      const width = Math.max(200, Math.min(340, pr.right - Math.max(pr.left + 12, cr.left) - 12));
      const below = cr.bottom - pr.top + gap;
      const above = pr.bottom - cr.top + gap;
      // Rough preview height; flip above the chip when the space below is short.
      const estHeight = 220;
      if (cr.bottom + gap + estHeight > pr.bottom && above > estHeight) {
        this.previewStyle = {
          left: `${left}px`,
          bottom: `${Math.max(12, above)}px`,
          width: `${width}px`,
        };
      } else {
        this.previewStyle = {
          left: `${left}px`,
          top: `${Math.min(below, Math.max(12, pr.bottom - 12))}px`,
          width: `${width}px`,
        };
      }
    },
    reduceMotion() {
      return window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    trackAnim(animation) {
      if (!this._spAnims) this._spAnims = new Set();
      this._spAnims.add(animation);
      return animation;
    },
    untrackAnim(animation) {
      if (this._spAnims) this._spAnims.delete(animation);
    },
    // The streamed answer fades/settles in once, when the first tokens arrive,
    // so the text reads as "forming" rather than appearing out of nowhere.
    beforeAnswerEnter(el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(6px)';
    },
    enterAnswer(el, done) {
      if (this.reduceMotion()) {
        el.style.opacity = '';
        el.style.transform = '';
        done();
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        el.style.opacity = '';
        el.style.transform = '';
        this.untrackAnim(animation);
        done();
      };
      const animation = this.trackAnim(animate(el, {
        opacity: [0, 1],
        transform: ['translateY(6px)', 'translateY(0px)'],
      }, {
        type: 'spring',
        bounce: 0,
        duration: 0.4,
        onComplete: finish,
      }));
      // Safety net: never leave the answer invisible if the spring stalls.
      window.setTimeout(finish, 1000);
    },
    leaveAnswer(el, done) {
      if (this.reduceMotion()) {
        done();
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        this.untrackAnim(animation);
        done();
      };
      const animation = this.trackAnim(animate(el, {
        opacity: [1, 0],
        transform: ['translateY(0px)', 'translateY(-4px)'],
      }, {
        type: 'spring',
        bounce: 0,
        duration: 0.18,
        onComplete: finish,
      }));
      window.setTimeout(finish, 400);
    },
    // Citation chips spring in with a small stagger once the answer settles.
    beforeChipEnter(el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(4px)';
    },
    enterChip(el, done) {
      if (this.reduceMotion()) {
        el.style.opacity = '';
        el.style.transform = '';
        done();
        return;
      }
      const index = Math.max(0, Array.from(el.parentNode ? el.parentNode.children : []).indexOf(el));
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        el.style.opacity = '';
        el.style.transform = '';
        this.untrackAnim(animation);
        done();
      };
      const animation = this.trackAnim(animate(el, {
        opacity: [0, 1],
        transform: ['translateY(4px)', 'translateY(0px)'],
      }, {
        type: 'spring',
        bounce: 0,
        duration: 0.3,
        delay: index * 0.045,
        onComplete: finish,
      }));
      window.setTimeout(finish, 1000);
    },
    leaveChip(el, done) {
      if (this.reduceMotion()) {
        done();
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        this.untrackAnim(animation);
        done();
      };
      const animation = this.trackAnim(animate(el, {
        opacity: [1, 0],
        transform: ['translateY(0px)', 'translateY(-2px)'],
      }, {
        type: 'spring',
        bounce: 0,
        duration: 0.12,
        onComplete: finish,
      }));
      window.setTimeout(finish, 400);
    },
    onOverlayPointerDown(e) {
      this.overlayPointerStartedOutside = !this.isInsidePanel(e.target);
      this.overlayPointerEndedOutside = false;
    },
    onOverlayPointerUp(e) {
      this.overlayPointerEndedOutside = !this.isInsidePanel(e.target);
    },
    onOverlayClick(e) {
      if (this.overlayPointerStartedOutside && this.overlayPointerEndedOutside) {
        this.close();
      } else {
        e.preventDefault();
        e.stopPropagation();
      }
      this.overlayPointerStartedOutside = false;
      this.overlayPointerEndedOutside = false;
    },
    isInsidePanel(target) {
      return !!(target && target.closest && target.closest('.sp-panel'));
    },
    onKey(e) {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        e.preventDefault();
        this.toggle('shortcut');
      } else if (e.key === 'Escape' && this.open) {
        this.close();
      } else if (e.key === '/' && !this.open && !this.isTyping(e)) {
        e.preventDefault();
        this.openPalette('shortcut');
      }
    },
    isTyping(e) {
      const t = e.target;
      return t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    },
    async loadIndex() {
      if (this.loaded) return;
      try {
        const res = await fetch('/assets/search-index.json', { cache: 'no-cache' });
        this.items = await res.json();
        this.fuse = new Fuse(this.items, {
          keys: [{ name: 'title', weight: 0.7 }, { name: 'body', weight: 0.3 }],
          threshold: 0.3,
          ignoreLocation: true,
          minMatchCharLength: 2,
        });
        this.loaded = true;
      } catch (err) {
        console.error('search index load failed:', err);
      }
    },
    toggle(via = 'shortcut') {
      this.open ? this.close() : this.openPalette(via);
    },
    openPalette(via = 'shortcut') {
      this.resetMobileClose();
      this.viewSession = loadAskSession();
      this.instantOpen = via === 'shortcut';
      this.open = true;
      this.viewError = '';
      this.markOpened(via);
      this.sampleVisible = true;
      this.loadIndex();
      this.lockScroll();
      this.warmSemantic();
      if (this.mode === 'view') this.loadRecentSharedViews();
      this.focusInputAfterOpen();
    },
    markOpened(via) {
      this._openedAt = Date.now();
      logEvent('palette_open', { via, mode: this.mode });
    },
    openAsk(prefill) {
      this.resetMobileClose();
      this.viewSession = loadAskSession();
      this.instantOpen = false;
      this.open = true;
      this.markOpened(prefill ? 'prefill' : 'ask_button');
      this.sampleVisible = true;
      this.loadIndex();
      this.lockScroll();
      this.mode = 'ask';
      this.activeIndex = 0;
      this.askAnswer = '';
      this.askSources = [];
      this.askError = '';
      this.viewError = '';
      this.viewOfferVisible = false;
      this.viewRouting = 'none';
      this.warmSemantic();
      if (prefill && prefill.trim()) {
        this.query = prefill.trim();
        this.$nextTick(() => this.askQuestion());
      } else {
        this.query = '';
        this.focusInputAfterOpen();
      }
    },
    async openViewFromAsk(question, { surface = 'palette', returnContext } = {}) {
      if (!String(question || '').trim() || isViewComposing()) return;
      if (surface === 'home') {
        this.viewSession = loadAskSession();
        this.query = String(question).trim();
        this.mode = 'view';
        this.viewError = '';
        this.startViewBuild({ askOfferConfirmed: true, surface, returnContext });
        return;
      }
      this.openView(question);
      await this.$nextTick();
      this.startViewBuild({ askOfferConfirmed: true });
    },
    openView(prefill = '') {
      this.resetMobileClose();
      this.externalViewJobId = '';
      this.viewSession = loadAskSession();
      this.instantOpen = false;
      this.open = true;
      this.mode = 'view';
      this.markOpened(prefill ? 'view_prefill' : 'view_button');
      this.loadIndex();
      this.lockScroll();
      this.activeIndex = 0;
      this.viewError = '';
      this.viewOfferVisible = false;
      this.viewRouting = 'none';
      this.query = String(prefill || '').trim();
      this.warmSemantic();
      this.loadRecentSharedViews();
      this.focusInputAfterOpen();
    },
    openViewJob(task) {
      if (!task?.jobId) return;
      const ownRequest = !!this._viewAbortController && this._viewComposeContext?.surface !== 'home';
      const snapshot = (ownRequest ? this._viewComposeContext?.returnContext : task.returnContext)?.palette;
      this.resetMobileClose();
      this.viewSession = loadAskSession();
      this.instantOpen = false;
      this.open = true;
      this.mode = snapshot?.mode || 'view';
      this.markOpened('view_progress_notice');
      this.loadIndex();
      this.lockScroll();
      this.activeIndex = snapshot?.activeIndex || 0;
      this.viewError = '';
      this.viewOfferVisible = false;
      this.viewRouting = 'none';
      this.query = String(snapshot?.query || task.message || '').trim();
      this.viewBusy = true;
      this.viewStartedAt = Number(task.startedAt) || Date.now();
      // The original request still owns completion while this component lives.
      // Only a resumed job needs the App's external completion handler.
      this.externalViewJobId = ownRequest ? '' : task.jobId;
      this.$nextTick(() => requestAnimationFrame(() => {
        if (!this.open || !snapshot) return;
        if (this.$refs.results) {
          this.$refs.results.scrollTop = snapshot.resultsTop || 0;
          this.$refs.results.scrollLeft = snapshot.resultsLeft || 0;
        }
        if (this.$refs.panel) this.$refs.panel.scrollTop = snapshot.panelTop || 0;
      }));
    },
    async finishExternalViewJob(jobId) {
      if (!jobId || this.externalViewJobId !== jobId) return false;
      this.externalViewJobId = '';
      this.viewBusy = false;
      this.viewStartedAt = 0;
      if (!this.open) return false;
      await this.enterComposedView();
      return true;
    },
    failExternalViewJob(jobId, message) {
      if (!jobId || this.externalViewJobId !== jobId) return false;
      this.externalViewJobId = '';
      this.viewBusy = false;
      this.viewStartedAt = 0;
      if (!this.open) return false;
      this.viewError = String(message || 'Composition is temporarily unavailable. Please try again.').slice(0, 300);
      return true;
    },
    close() {
      this.mobileModeMenuOpen = false;
      if (this.embedded) {
        if (this._viewAbortController) {
          this._viewAbortController.abort();
          this._viewAbortController = null;
        }
        this._viewSeq += 1;
        this.viewBusy = false;
        return;
      }
      const mobileSurface = window.matchMedia('(max-width: 1100px)').matches;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.mobileClosing) return;
      if (mobileSurface && !reducedMotion && !this.mobileClosing) {
        this.mobileClosing = true;
        window.clearTimeout(this._mobileCloseTimer);
        this._mobileCloseTimer = window.setTimeout(() => this.finishClose(), 330);
        return;
      }
      this.finishClose();
    },
    finishClose() {
      if (this.open && this._viewAbortController && this._viewComposeContext?.surface === 'palette') {
        this._viewComposeContext.returnContext.palette = this.capturePaletteForView();
      }
      window.clearTimeout(this._mobileCloseTimer);
      this._mobileCloseTimer = null;
      this.open = false;
      this.mobileClosing = false;
      this.unlockScroll();
    },
    resetMobileClose() {
      window.clearTimeout(this._mobileCloseTimer);
      this._mobileCloseTimer = null;
      this.mobileClosing = false;
    },
    reopenCompletedAsk() {
      this.resetMobileClose();
      this.instantOpen = false;
      this.open = true;
      this.mode = 'ask';
      this.markOpened('completion_notice');
      this.loadIndex();
      this.lockScroll();
      this.focusInputAfterOpen();
    },
    focusInputAfterOpen() {
      if (window.matchMedia && window.matchMedia('(max-width: 760px)').matches) return;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    capturePaletteForView() {
      const results = this.$refs.results;
      const panel = this.$refs.panel;
      const input = this.$refs.input;
      return {
        route: this.$route.fullPath,
        mode: this.mode,
        query: this.query,
        savedQueries: { ...this.savedQueries },
        activeIndex: this.activeIndex,
        mapProjection: this.mapProjection,
        pageX: window.scrollX,
        pageY: window.scrollY,
        resultsTop: results ? results.scrollTop : 0,
        resultsLeft: results ? results.scrollLeft : 0,
        panelTop: panel ? panel.scrollTop : 0,
        inputStart: input && typeof input.selectionStart === 'number' ? input.selectionStart : null,
        inputEnd: input && typeof input.selectionEnd === 'number' ? input.selectionEnd : null,
        inputFocused: document.activeElement === input,
      };
    },
    suspendPaletteForView() {
      this._viewReturnSnapshot = this.capturePaletteForView();
      this._viewRestoreTimers.forEach((timer) => window.clearTimeout(timer));
      this._viewRestoreTimers = [];
      this._viewAbortController = null;
      this.viewBusy = false;
      this.open = false;
      this.unlockScroll();
    },
    restorePaletteAfterView(snapshot) {
      this.viewSession = loadAskSession();
      this.mode = snapshot.mode || this.mode;
      this.query = typeof snapshot.query === 'string' ? snapshot.query : this.query;
      this.savedQueries = snapshot.savedQueries ? { ...snapshot.savedQueries } : this.savedQueries;
      this.activeIndex = Number.isInteger(snapshot.activeIndex) ? snapshot.activeIndex : this.activeIndex;
      this.mapProjection = snapshot.mapProjection || this.mapProjection;
      this.instantOpen = true;
      this.open = true;
      window.scrollTo({ left: snapshot.pageX, top: snapshot.pageY, behavior: 'auto' });
      this.lockScroll();

      const restore = () => {
        if (this._viewReturnSnapshot !== snapshot || !this.open) return;
        window.scrollTo({ left: snapshot.pageX, top: snapshot.pageY, behavior: 'auto' });
        const results = this.$refs.results;
        const panel = this.$refs.panel;
        const input = this.$refs.input;
        if (results) {
          results.scrollTop = snapshot.resultsTop;
          results.scrollLeft = snapshot.resultsLeft;
        }
        if (panel) panel.scrollTop = snapshot.panelTop;
        if (input && snapshot.inputStart != null) {
          input.setSelectionRange(snapshot.inputStart, snapshot.inputEnd ?? snapshot.inputStart);
        }
        if (input && snapshot.inputFocused) input.focus({ preventScroll: true });
        if (this.hoveredSourceId) this.updateAskPreviewPosition();
      };

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          restore();
          this._viewRestoreTimers = [80, 240].map((delay) => window.setTimeout(restore, delay));
          const finishTimer = window.setTimeout(() => {
            if (this._viewReturnSnapshot === snapshot) this._viewReturnSnapshot = null;
            this._viewRestoreTimers = [];
          }, 280);
          this._viewRestoreTimers.push(finishTimer);
        });
      });
    },
    move(dir) {
      const n = this.flatList.length;
      if (!n) return;
      this.activeIndex = (this.activeIndex + dir + n) % n;
      this.$nextTick(() => {
        const el = this.rowRefs[this.activeIndex];
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
      });
    },
    isComposing(e) {
      return this.composing || e.isComposing || e.keyCode === 229;
    },
    onEnter(e) {
      if (this.isComposing(e)) return;
      e.preventDefault();
      if (this.mode === 'ask') this.askQuestion();
      else if (this.mode === 'view') this.startViewBuild();
      else if (this.mode === 'map') this.placeMapQuery();
      else this.choose();
    },
    onArrow(e, dir) {
      if (this.isComposing(e)) return;
      if (this.mode !== 'search') return;
      e.preventDefault();
      this.move(dir);
    },
    onInputKeydown(e) {
      if (this.isComposing(e)) return;
      if (e.key !== 'Alt' || e.repeat) return;
      e.preventDefault();
      this.cycleMode();
    },
    cycleMode() {
      const modes = ['ask', 'view', 'search', 'map'];
      const i = modes.indexOf(this.mode);
      this.setMode(modes[(i + 1) % modes.length]);
    },
    choose() {
      const target = this.flatList[this.activeIndex];
      if (target) this.openResult(target, 'enter');
    },
    // Log a search only once the query settles, so typing "melbourne" produces
    // one row rather than nine. Re-fires when the semantic leg lands late.
    scheduleSearchLog() {
      window.clearTimeout(this._searchLogTimer);
      this._searchLogTimer = window.setTimeout(() => this.reportSearch(), 900);
    },
    reportSearch() {
      const q = this.query.trim();
      if (!q || this.mode !== 'search' || this.semBusy) return;
      if (this._loggedSearchQuery === q) return;
      this._loggedSearchQuery = q;
      this._searchId = newId();
      this._searchAt = Date.now();

      const semById = new Map(this.semHits.map((h, i) => [h.id, { rank: i + 1, score: h.score }]));
      const lex = this.fuse ? this.fuse.search(q).slice(0, 20).map(r => r.item.id) : [];
      const lexById = new Map(lex.map((id, i) => [id, i + 1]));
      logSearch({
        search_id: this._searchId,
        surface: 'palette',
        query: q,
        n_results: this.results.length,
        n_lex: lex.length,
        n_sem: this.semHits.length,
        sem_top_score: this.semHits.length ? Number(this.semHits[0].score.toFixed(5)) : null,
        sem_error: this.semError || null,
        ms_since_open: this._openedAt ? Date.now() - this._openedAt : null,
        results: this.results.map((item, i) => ({
          chunk_id: String(item.id),
          route: item.route,
          anchor: item.anchor || null,
          page: item.page,
          rank: i + 1,
          lex_rank: lexById.get(item.id) || null,
          sem_rank: semById.get(item.id) ? semById.get(item.id).rank : null,
          sem_score: semById.get(item.id) ? Number(semById.get(item.id).score.toFixed(5)) : null,
        })),
      });
    },
    openResult(item, via) {
      const rank = this.results.findIndex(r => r.id === item.id) + 1;
      logSearchClick({
        search_id: this._searchId || null,
        query: this.query.trim(),
        chunk_id: String(item.id),
        route: item.route,
        anchor: item.anchor || null,
        rank: rank || null,
        via,
        ms_to_click: this._searchAt ? Date.now() - this._searchAt : null,
      });
      this.go(item.route, item.anchor, true);
    },
    openAskSource(source) {
      logEvent('ask_source_click', {
        chunk_id: source.chunk_id || null,
        route: source.route,
        anchor: source.anchor || null,
        title: source.displayTitle || source.title || null,
        surface: 'palette',
      }, this._lastAskEventId || null);
      this.go(source.route, source.anchor);
    },
    openPublicViews() {
      logEvent('public_views_opened', { surface: 'palette', mode: 'view' });
      this.go('/views', 'public-views-collection');
    },
    setRowRef(el, i) {
      if (el) this.rowRefs[i] = el;
      else delete this.rowRefs[i];
    },
    rememberSourceReturn() {
      if (this.embedded || !this.open) return;
      const snapshot = this.capturePaletteForView();
      snapshot.askState = {
        askAnswer: this.askAnswer, askSources: this.askSources, askError: this.askError,
        askStatus: this.askStatus, viewRouting: this.viewRouting, viewOfferVisible: this.viewOfferVisible,
        groundedFollowUps: this.groundedFollowUps,
      };
      this._sourceReturnSnapshot = snapshot;
      prepareAskSourceReturn({ route: snapshot.route, label: this.mode === 'ask' ? 'Ask' : 'Search',
        scrollY: snapshot.pageY, askReturn: { surface: 'palette', snapshot } });
    },
    restoreFromSource(snapshot) {
      if (!snapshot) return;
      this._sourceReturnSnapshot = null;
      if (snapshot.askState) Object.assign(this, snapshot.askState);
      this.resetMobileClose();
      this._viewReturnSnapshot = snapshot;
      this.restorePaletteAfterView(snapshot);
    },
    go(route, anchor, projectWebsite = false) {
      const destination = projectWebsite ? projectWebsiteTarget(route, anchor) : sourceRouteTarget(route, anchor);
      if (destination?.href) {
        window.open(destination.href, '_blank', 'noopener');
        return;
      }
      const samePage = this.$route.path === route;
      this.rememberSourceReturn();
      if (this.embedded) this.close();
      else this.finishClose();
      if (samePage) consumeAskSourceDeparture();

      if (!samePage) {
        const target = { path: route };
        if (anchor) target.hash = `#${anchor}`;
        this.$router.push(target).catch(() => {});
      }

      this.$nextTick(() => this.scrollTo(anchor, 0, samePage));
    },
    scrollTo(anchor, attempt, samePage) {
      if (!samePage) {
        // Cross-page landings are handled centrally once the new page has
        // mounted; here we only flash the entry when it appears.
        if (!anchor) return;
        // Group auto-expansion is disabled (groups are always expanded).
        // if (typeof window.__expandEntryForLanding === 'function') window.__expandEntryForLanding(anchor);
        const el = document.getElementById(anchor);
        if (el) {
          this.flash(el);
          return;
        }
        if (attempt < 25) setTimeout(() => this.scrollTo(anchor, attempt + 1, samePage), 120);
        return;
      }
      if (!anchor) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      if (attempt === 0) {
        const collection = document.getElementById(anchor)?.closest('.collection-browser');
        if (collection) {
          collection.dispatchEvent(new CustomEvent('collection-target', { detail: anchor }));
          this.$nextTick(() => this.scrollTo(anchor, 1, samePage));
          return;
        }
      }
      if (settleScrollToId(anchor)) {
        const el = document.getElementById(anchor);
        this.flash(el);
        return;
      }
      if (attempt < 25) setTimeout(() => this.scrollTo(anchor, attempt + 1, samePage), 120);
    },
    flash(el) {
      el.classList.add('search-flash');
      setTimeout(() => el.classList.remove('search-flash'), 1700);
    },
    lockScroll() {

      this._releaseScroll ||= lockBodyScroll();
    },
    unlockScroll() {
      this._releaseScroll?.();this._releaseScroll=null;
    },
    escapeHtml(s) {
      return String(s)
        .split('&').join('&amp;')
        .split('<').join('&lt;')
        .split('>').join('&gt;')
        .split(String.fromCharCode(34)).join('&quot;');
    },
    escapeRegExp(text) {
      return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    words(text) {
      const out = [];
      let word = '';
      for (const c of String(text).toLowerCase()) {
        const ok = (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === '_';
        if (ok) word += c;
        else if (word) { out.push(word); word = ''; }
      }
      if (word) out.push(word);
      return out;
    },
    tokens() {
      const all = this.words(this.query).filter(t => t.length >= 2);
      const meaningful = all.filter(t => !STOP.has(t));
      return meaningful.length ? meaningful : all;
    },
    highlight(text) {
      const escaped = this.escapeHtml(text);
      const terms = [...new Set(this.tokens())]
        .sort((a, b) => b.length - a.length)
        .map((term) => this.escapeRegExp(term));
      if (!terms.length) return escaped;

      const re = new RegExp(`(${terms.join('|')})`, 'gi');
      return escaped.replace(re, '<mark>$1</mark>');
    },
    displayTitle(item) {
      return sourceDisplayTitle(item);
    },
    askDisplayTitle(item) {
      return sourceDisplayTitle(item);
    },
    searchDisplayBody(body) {
      return String(body || '')
        .replace(/\bEnglish summary:\s*/g, '')
        .replace(/中文摘要:\s*/g, '')
        .replace(/\bEnglish full text:\s*/g, '')
        .replace(/中文全文:\s*/g, '')
        .trim();
    },
    askContextText(source) {
      let text = cleanAskContextText(source.body);
      if (source.route !== '/fragments') return text;
      return this.searchDisplayBody(text);
    },
    snippet(body) {
      const lower = body.toLowerCase();
      let pos = -1;
      for (const t of [...this.tokens()].sort((a, b) => b.length - a.length)) {
        const found = lower.indexOf(t);
        if (found >= 0) { pos = found; break; }
      }
      if (pos < 0) return '';
      const start = Math.max(0, pos - 40);
      let s = body.slice(start, start + 170);
      if (start > 0) s = `…${s}`;
      if (start + 170 < body.length) s = `${s}…`;
      return this.highlight(s);
    },
    leadSnippet(body) {
      let s = body.slice(0, 170);
      if (body.length > 170) s = `${s}…`;
      return this.highlight(s);
    },
    setMode(m) {
      this.mobileModeMenuOpen = false;
      if (this.viewBusy) return;
      if (this.mode === m) return;
      logEvent('palette_mode', { from: this.mode, to: m });
      this.savedQueries[this.mode] = this.query;
      this.mode = m;
      this.query = this.savedQueries[m] || '';
      this.activeIndex = 0;
      this.sampleVisible = true;
      if (m === 'search') {
        if (this.query.trim()) { this.warmSemantic(); this.scheduleSemantic(); }
      } else if (m === 'ask' || m === 'view') {
        this.warmSemantic();
        if (m === 'view') this.loadRecentSharedViews();
      } else {
        this.$nextTick(() => {
          if (this.query.trim()) this.placeMapQuery();
        });
      }
    },
    selectMobileMode(mode) {
      this.mobileModeMenuOpen = false;
      this.setMode(mode);
    },
    clearQuery() {
      this.query = '';
      if (this.mode === 'ask') {
        this.askAnswer = '';
        this.askSources = [];
        this.askError = '';
        this.groundedFollowUps = null;
      }
      if (this.mode === 'view') this.viewError = '';
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    placeMapQuery() {
      const map = this.$refs.paletteMap;
      if (map && map.placeQuery) map.placeQuery(this.query);
    },
    setMapProjection(mode) {
      this.mapProjection = mode;
      const map = this.$refs.paletteMap;
      if (map && map.setViewMode) map.setViewMode(mode);
    },
    useCurrentSample() {
      this.query = this.currentSamplePrompt;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    async loadRecentSharedViews() {
      if (this.recentSharedViewsLoading) return;
      this.recentSharedViewsLoading = true;
      try {
        const data = await listPublicViews();
        this.recentSharedViews = (data.views || [])
          .slice()
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
          .slice(0, 3);
      } catch {
        this.recentSharedViews = [];
      } finally {
        this.recentSharedViewsLoading = false;
      }
    },
    async openSharedView(id) {
      if (!id) return;
      this.suspendPaletteForView();
      await this.$router.push({ path: `/view/${id}` }).catch(() => {});
    },
    askFollowUp(q) {
      const question = String(q || '').trim();
      if (!question || this.askBusy || this.viewBusy) return;
      this.query = question;
      this.hoveredSourceId = null;
      logEvent('ask_followup_click', { question, surface: 'palette' }, this._lastAskEventId || null);
      this.askQuestion();
    },
    async buildViewFromCurrent() {
      if (isViewComposing()) return;
      this.viewSession = loadAskSession();
      if (this.canReturnToView) {
        logEvent('view_return_clicked', { surface: 'palette' });
        await this.enterComposedView();
        return;
      }
      logEvent('view_offer_clicked', { surface: 'palette' });
      this.query = this.query || this.savedQueries.ask || '';
      this.startViewBuild({ askOfferConfirmed: true });
    },
    async startViewBuild({ askOfferConfirmed = false, surface = 'palette', returnContext } = {}) {
      if (this.viewBusy) return;
      // Site-wide lock: only one View may compose at a time across the
      // homepage, the palette, and the /views composer.
      if (isViewComposing()) {
        this.viewError = 'A View is already composing. You can follow its progress here.';
        return;
      }
      const seq = ++this._viewSeq;
      if (this._viewAbortController) this._viewAbortController.abort();
      const controller = new AbortController();
      this._viewAbortController = controller;
      this.viewSession = loadAskSession();
      this.viewError = '';
      this.askError = '';
      this.viewBusy = true;
      this.viewStartedAt = Date.now();
      beginViewCompose(surface, this.viewStartedAt, this.query);
      this.askStatus = '';
      const completionContext = {
        originRoute: this.$route.fullPath,
        wasOpen: this.open,
        surface,
        askOfferConfirmed,
        returnContext: returnContext || (surface === 'home'
          ? { pageY: window.scrollY || 0 }
          : { palette: this.capturePaletteForView() }),
      };
      this._viewComposeContext = completionContext;
      try {
        await this.buildViewRequest(controller.signal, seq, completionContext);
      } catch (err) {
        if (err?.name !== 'AbortError' && seq === this._viewSeq) {
          this.viewError = String(err?.message || err).slice(0, 240);
          if (surface === 'home') {
            window.dispatchEvent(new CustomEvent('home-view-error', { detail: { message: this.viewError } }));
            if (this.$route.fullPath !== completionContext.originRoute) this.$emit('ai-complete', { kind: 'view', label: 'Open View to try again.', surface, failed: true, prefill: this.query });
          }
          logEvent('view_build_error', {
            surface: 'palette',
            error: String(err?.message || err).slice(0, 160),
            code: String(err?.code || '').slice(0, 80),
            http_status: Number(err?.httpStatus) || 0,
          });
        }
      } finally {
        if (seq === this._viewSeq) {
          this.viewBusy = false;
          this.viewStartedAt = 0;
          this._viewAbortController = null;
          endViewCompose(surface);
        }
      }
    },
    async buildViewRequest(signal, seq, completionContext = {}) {
      const rawMessage = this.query.trim();
      const message = cleanViewIntent(rawMessage);
      if (!message) return;
      if (isDevelopMode(rawMessage)) {
        const fakeJob = {
          version: 1,
          jobId: `develop-${Date.now()}-${seq}`,
          status: 'composing',
          message: DEVELOP_VIEW_LABEL,
          surface: completionContext.surface || 'palette',
          originRoute: completionContext.originRoute,
          returnContext: completionContext.returnContext,
          startedAt: Date.now(),
        };
        window.dispatchEvent(new CustomEvent('site-view-job', { detail: fakeJob }));
        await developViewDelay(signal);
        if (signal.aborted || seq !== this._viewSeq) throw new DOMException('Aborted', 'AbortError');
        window.dispatchEvent(new CustomEvent('site-view-job', {
          detail: { ...fakeJob, status: 'complete', completedAt: Date.now(), payload: null },
        }));
        const stayedWithRequest = this.stayedWithViewRequest(completionContext);
        if (stayedWithRequest) {
          await this.enterComposedView(DEVELOP_VIEW_ROUTE, { restorePalette: this.open });
        } else if (!this.embedded) {
          this.$emit('ai-complete', {
            kind: 'view',
            label: DEVELOP_VIEW_LABEL,
            originRoute: completionContext.originRoute,
            surface: completionContext.surface || 'palette',
            route: DEVELOP_VIEW_ROUTE,
          });
        }
        return null;
      }
      const existingView = recentDuplicateViewQuestion(message);
      if (existingView) {
        // The exact same question was composed within the last three days:
        // reuse the existing View instead of regenerating it.
        const stayedWithRequest = this.stayedWithViewRequest(completionContext);
        if (stayedWithRequest) {
          await this.enterComposedView('/view', { restorePalette: this.open });
        } else if (!this.embedded) {
          this.$emit('ai-complete', {
            kind: 'view',
            label: existingView.title || message,
            originRoute: completionContext.originRoute,
            surface: completionContext.surface || 'palette',
          });
        }
        return null;
      }
      const meta = {
        surface: completionContext.surface || 'palette',
        origin_route: completionContext.originRoute,
        ask_offer_confirmed: completionContext.askOfferConfirmed === true,
        event_id: newId(),
        session_id: sessionId(),
        turn_index: this.viewSession && this.viewSession.turns ? this.viewSession.turns.length + 1 : 1,
      };
      this._lastViewEventId = meta.event_id;
      logEvent('view_build_requested', { surface: completionContext.surface || 'palette', question: message }, meta.event_id);
      const directView = this.mode === 'view' && !completionContext.askOfferConfirmed;
      const conversation = directView ? [] : recentAskTurns(this.viewSession, 6);
      const sources = await candidatesForView({
        message,
        session: directView ? null : this.viewSession,
        latestMessage: message,
      });
      if (signal.aborted || seq !== this._viewSeq) throw new DOMException('Aborted', 'AbortError');
      const response = await requestView({
        message,
        conversation,
        sources,
        meta,
        signal,
        returnContext: completionContext.returnContext,
      });
      if (signal.aborted || seq !== this._viewSeq) throw new DOMException('Aborted', 'AbortError');
      const sanitizedResponse = sanitizeViewResponse(response, sources);
      const validation = validateViewResponse(sanitizedResponse, sources);
      if (!validation.ok) throw new Error(validation.error || 'The composed view did not pass validation.');
      if (sanitizedResponse.action === 'answer') {
        this.viewError = String(sanitizedResponse.message || 'Try a question, theme, or perspective that can be explored across Henry’s site.').slice(0, 300);
        logEvent('view_build_declined', {
          surface: completionContext.surface || 'palette',
          question: message,
          message: this.viewError,
        }, meta.event_id);
        return null;
      }
      if (sanitizedResponse.action !== 'replace_view') {
        throw new Error('The composer did not return a complete view.');
      }

      const normalized = {
        action: sanitizedResponse.action,
        message: response.message || '',
        source_ids: response.source_ids || [],
        view: validation.view || sanitizedResponse.view || null,
      };

      this.viewSession = addAskTurn(this.viewSession, {
        role: 'user',
        text: message,
        source_ids: [],
      });
      const assistantSourceIds = normalized.action === 'replace_view'
        ? collectViewSourceIds(normalized.view)
        : normalized.source_ids;
      this.viewSession = addAskTurn(this.viewSession, {
        role: 'assistant',
        text: normalized.message || (normalized.action === 'replace_view' ? 'View updated.' : ''),
        source_ids: assistantSourceIds,
      });

      if (!normalized.view) throw new Error('Composition returned no view.');
      logEvent('view_build_success', {
        surface: completionContext.surface || 'palette',
        n_sources: collectViewSourceIds(normalized.view).length,
        n_sections: normalized.view.sections ? normalized.view.sections.length : 0,
      }, meta.event_id);
      this.viewSession = setAskView(this.viewSession, normalized.view);

      const currentRoute = this.$route.fullPath;
      const currentShareId = String(this.$route.params?.shareId || '');
      const previousOrigin = this.viewSession?.origin?.route || '/home';
      const originRoute = completionContext.originRoute || (isGeneratedViewPath(this.$route.path)
        ? (currentShareId ? currentRoute : previousOrigin)
        : currentRoute);
      this.viewSession = setAskOrigin(this.viewSession, originRoute);
      const stayedWithRequest = this.stayedWithViewRequest(completionContext);
      if (stayedWithRequest) {
        await this.enterComposedView('/view', { restorePalette: this.open });
      } else if (!this.embedded) {
        this.$emit('ai-complete', {
          kind: 'view',
          label: normalized.view.title || message,
          originRoute: completionContext.originRoute,
          surface: completionContext.surface || 'palette',
        });
      }
      return normalized;
    },
    stayedWithViewRequest(context) {
      if (context.surface === 'home') return ['/', '/home'].includes(this.$route.path);
      return this.open && !this.mobileClosing;
    },
    async enterComposedView(route = '/view', { restorePalette = true } = {}) {
      this.viewDeparting = true;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!reducedMotion) {
        await new Promise((resolve) => window.setTimeout(resolve, 105));
      }
      if (restorePalette) this.suspendPaletteForView();
      else this.finishClose();
      this.viewDeparting = false;
      await this.$router.push({ path: route }).catch(() => {});
    },
    nextRandomSampleIndex() {
      const count = this.samplePrompts.length;
      if (count <= 1) return this.sampleIndex;

      let next = Math.floor(Math.random() * (count - 1));
      if (next >= this.sampleIndex) next += 1;
      return next;
    },
    rotateSample() {
      if (!this.open || this.mode !== 'ask' || this.query.trim() || this.answerText || this.askBusy || this.askError) return;
      this.sampleVisible = false;
      window.clearTimeout(this._sampleFadeTimer);
      this._sampleFadeTimer = window.setTimeout(() => {
        this.sampleIndex = this.nextRandomSampleIndex();
        this.$nextTick(() => { this.sampleVisible = true; });
      }, 260);
    },
    async askQuestion() {
      const q = this.query.trim();
      if (!q || !this.askReady) return;
      const seq = ++this._askSeq;
      this.viewSession = loadAskSession();
      if (looksLikeViewBuildRequest(q)) {
        this.askAnswer = '';
        this.askSources = [];
        this.groundedFollowUps = null;
        await this.startViewBuild();
        return;
      }
      this.askBusy = true;
      this.askError = '';
      this.askAnswer = '';
      this.askSources = [];
      this.groundedFollowUps = null;
      this.viewOfferVisible = false;
      this.viewRouting = 'none';
      this.viewError = '';

      const telemetry = newAskContext('palette');
      this._lastAskEventId = telemetry.event_id;
      const startedAt = Date.now();
      let firstTokenAt = null;
      let trace = null;
      let payloadLength = 0;
      let payloadChars = 0;
      let failure = '';
      // Local copies: a newer question resets the component state, and the
      // superseded ask still deserves an accurate row.
      let answer = '';
      let sources = [];
      const completionContext = {
        originRoute: this.$route.fullPath,
        wasOpen: this.open,
      };

      try {
        this.askStatus = 'Thinking…';
        const recentSourceIds = askSourceIdsInRecent(this.viewSession, 18);
        const contexts = diversifyAskWritingContexts(
          await allAskContexts(q, this.items),
          q,
          telemetry.event_id,
          recentSourceIds,
        )
          .map(source => ({
            ...source,
            text: this.askContextText(source),
          }))
          .filter(source => source.text);
        trace = takeRetrievalTrace();
        sources = contexts;
        if (seq !== this._askSeq) return;
        this.askSources = contexts;
        const payload = contexts.map(c => ({
          page: c.page,
          title: this.askDisplayTitle(c),
          text: c.text,
          source_id: c.source_id || c.chunk_id || String(c.id || ''),
        }));
        payloadLength = payload.length;
        payloadChars = payload.reduce((sum, c) => sum + c.text.length, 0);
        const stream = askStream(q, payload, {
          meta: {
            event_id: telemetry.event_id,
            session_id: telemetry.session_id,
            turn_index: telemetry.turn_index,
            surface: telemetry.surface,
            recent_source_ids: recentSourceIds,
          },
          onFirstToken: () => { firstTokenAt = Date.now(); },
        });
        for await (const tok of stream) {
          if (seq !== this._askSeq) return;
          answer += tok;
          this.askAnswer += tok;
        }
        if (!askResponseIsComplete(answer)) throw new Error('incomplete ask response');
        if (seq !== this._askSeq) return;

        const effectiveRouting = askViewRouting(answer);
        this.viewRouting = effectiveRouting;
        if (effectiveRouting === 'build') {
          this.askAnswer = '';
          this.askSources = [];
          await this.startViewBuild();
          return;
        }

        this.viewSession = addAskTurn(this.viewSession, {
          role: 'user',
          text: q,
          source_ids: [],
        });
        this.viewSession = addAskTurn(this.viewSession, {
          role: 'assistant',
          text: answer,
          source_ids: citedSourcesFromAnswer(answer, sources)
            .map((s) => s.source_id)
            .filter(Boolean),
        });
        this.viewOfferVisible = effectiveRouting === 'offer';
        if (effectiveRouting === 'offer') logEvent('view_offer_shown', { surface: 'palette' });
        this.groundAiFollowUps(seq);
        if (!this.embedded && (!this.open || !completionContext.wasOpen || this.$route.fullPath !== completionContext.originRoute)) {
          this.$emit('ai-complete', {
            kind: 'ask',
            label: q,
            originRoute: completionContext.originRoute,
            surface: 'palette',
          });
        }
      } catch (e) {
        failure = String(e?.message || e).slice(0, 200);
        if (seq === this._askSeq) {
          this.askAnswer = '';
          this.askSources = [];
          this.askError = 'error';
        }
        console.error('ask failed:', e);
      } finally {
        if (seq === this._askSeq) this.askBusy = false;
        this.reportAsk(telemetry, {
          question: q,
          answer,
          sources,
          startedAt,
          firstTokenAt,
          trace,
          payloadLength,
          payloadChars,
          failure,
          aborted: seq !== this._askSeq,
        });
      }
    },
    async groundAiFollowUps(seq) {
      const questions = askAnswerFollowUps(this.askAnswer);
      if (!questions.length) {
        if (seq === this._askSeq) this.groundedFollowUps = [];
        return;
      }
      const grounded = await groundAskFollowUps(questions);
      if (seq !== this._askSeq) return;
      this.groundedFollowUps = grounded;
    },
    // One row per question describing what the browser saw: ranking internals,
    // timings, and which sources the model actually cited.
    reportAsk(telemetry, info) {
      const finishedAt = Date.now();
      const cited = info.answer
        ? citedSourcesFromAnswer(info.answer, info.sources).map(s => ({
          n: s.n,
          chunk_id: s.chunk_id,
          route: s.route,
          anchor: s.anchor || null,
          title: this.askDisplayTitle(s),
        }))
        : [];
      logAskClient(telemetry, {
        question: info.question,
        retrieval_ms: info.trace ? info.trace.retrieval_ms : null,
        ttft_ms: info.firstTokenAt ? info.firstTokenAt - info.startedAt : null,
        stream_ms: info.firstTokenAt ? finishedAt - info.firstTokenAt : null,
        total_ms: finishedAt - info.startedAt,
        answer_len: info.answer.length,
        aborted: info.aborted,
        error: info.failure || null,
        n_contexts_sent: info.payloadLength,
        contexts_chars: info.payloadChars,
        n_lex: info.trace ? info.trace.n_lex : null,
        n_sem: info.trace ? info.trace.n_sem : null,
        sem_top_score: info.trace ? info.trace.sem_top_score : null,
        cited,
        retrieval: info.trace ? info.trace.rows : [],
      });
    },
    hybridIds(q) {
      const K = 60;
      const W_LEX = 2;
      const W_SEM = 1;
      const score = new Map();
      const add = (ids, w) => ids.forEach((id, i) => score.set(id, (score.get(id) || 0) + w / (K + i)));
      if (this.fuse) add(this.fuse.search(q).slice(0, 20).map(r => r.item.id), W_LEX);
      add(this.semHits.map(h => h.id), W_SEM);
      return [...score.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id);
    },
    async warmSemantic() {
      if (this._semWarmed || this.semLoading) return;
      this.semLoading = true;
      this.semError = '';
      try {
        await ensureReady();
        this._semWarmed = true;
      } catch (e) {
        this.semError = 'offline';
        console.error('semantic warm-up failed:', e);
      } finally {
        this.semLoading = false;
      }
    },
    scheduleSemantic() {
      clearTimeout(this._semDebounce);
      this._semDebounce = setTimeout(() => this.runSemantic(), 250);
    },
    async runSemantic() {
      const q = this.query.trim();
      if (!q) { this.semHits = []; return; }
      const seq = ++this._semSeq;
      this.semQuerying = true;
      try {
        const hits = await semanticRank(q, 12);
        if (seq === this._semSeq) { this.semHits = hits; this.semError = ''; }
      } catch (e) {
        if (seq === this._semSeq) { this.semHits = []; this.semError = 'offline'; }
        console.error('semantic search failed:', e);
      } finally {
        if (seq === this._semSeq) this.semQuerying = false;
      }
    },
  },
};
</script>

<style scoped>
.sp-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12vh 20px 20px;
  background: rgba(24, 24, 28, 0.16);
  backdrop-filter: blur(5px) saturate(108%);
  -webkit-backdrop-filter: blur(5px) saturate(108%);
}
.sp-panel {
  position: relative;
  width: 100%;
  max-width: 600px;
  background: rgba(250, 250, 251, 0.94);
  border: 1px solid rgba(60, 60, 67, 0.16);
  border-radius: 0;
  box-shadow: none;
  backdrop-filter: blur(12px) saturate(120%);
  -webkit-backdrop-filter: blur(12px) saturate(120%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 68vh;
}

/* Inline embed: the same panel surface, but sitting in the page flow instead
   of a fixed overlay. No scroll lock, no shortcut hints, no mode switcher. */
.sp-embedded {
  position: static;
  /* A grid item still honours z-index even when its position is static. */
  z-index: auto;
  isolation: isolate;
  padding: 0;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

:global(body.dark-mode .sp-embedded) {
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

.sp-embedded .sp-panel {
  max-height: none;
  max-width: 600px;
  margin: 1.3rem auto 1.6rem;
  border: 0;
}

.sp-embedded .sp-results {
  max-height: 46vh;
  overscroll-behavior-y: auto;
}
.sp-embedded.sp-content-fit { align-items: stretch; }
.sp-content-fit .sp-panel { height: auto; max-height: none; margin: 0; }
.sp-content-fit .sp-results { flex: 1 0 auto; max-height: none; overflow: visible; }
.sp-panel-map {
  height: min(68vh, 600px);
  max-width: 600px;
}
.sp-input-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 68px;
  box-sizing: border-box;
  padding: 15px 18px;
  border-bottom: 1px solid rgba(60, 60, 67, 0.1);
}
.sp-input-icon {
  width: 1.25em;
  flex: 0 0 1.25em;
  text-align: center;
  color: #7f7f86;
  font-size: 1.05rem;
}
.sp-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: "PT Serif", Georgia, serif;
  min-width: 0;
  font-size: 1.16rem;
  color: #1f1f1f;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.sp-input::placeholder { color: #9b9ba1; text-overflow: ellipsis; }
.sp-input:disabled {
  color: #737378;
  cursor: progress;
  opacity: 0.62;
  -webkit-text-fill-color: #737378;
}
.sp-clear {
  display: grid;
  place-items: center;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #a4a4aa;
  cursor: pointer;
  transition: color 140ms ease, transform 140ms cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-clear:active { transform: scale(0.92); }
.sp-clear:disabled {
  cursor: default;
  opacity: 0.34;
  transform: none;
}
.sp-esc {
  appearance: none;
  display: grid;
  place-items: center;
  flex: 0 0 30px;
  box-sizing: border-box;
  width: 30px;
  height: 28px;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9a9a9a;
  border: 1px solid rgba(60, 60, 67, 0.14);
  border-radius: 7px;
  padding: 0;
  background: rgba(255, 255, 255, 0.48);
  cursor: pointer;
  transition:
    color 160ms ease,
    border-color 160ms ease,
    background-color 160ms ease,
    transform 140ms cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-esc:hover {
  color: #5f5f5f;
  border-color: rgba(60, 60, 67, 0.28);
  background: rgba(255, 255, 255, 0.72);
}
.sp-esc:active {
  color: #4a4a4a;
  background: rgba(255, 255, 255, 0.9);
  transform: scale(0.94);
}
.sp-close-mobile,
.sp-mobile-mode-trigger,
.sp-mobile-mode-menu {
  display: none;
}
.sp-modes {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 8px 14px 2px;
}
.sp-mode-segments {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border-radius: 0;
  background: rgba(118, 118, 128, 0.12);
}
.sp-mode {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  min-width: 62px;
  color: #76767d;
  background: transparent;
  border: 0;
  border-radius: 0;
  padding: 5px 10px;
  cursor: pointer;
  transition:
    color 160ms ease,
    background-color 180ms ease,
    transform 140ms cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-mode:hover {
  color: #333;
  background: rgba(255, 255, 255, 0.45);
}
.sp-mode:active { transform: scale(0.96); }
.sp-mode.active {
  color: #242426;
  background: rgba(255, 255, 255, 0.92);
}
.sp-mode:disabled {
  cursor: default;
  opacity: 0.46;
}
.sp-mode-status {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.06em;
  color: #b0b0b0;
  margin-left: 2px;
  line-height: 1;
}
.sp-projection-toggle {
  display: inline-flex;
  margin-left: auto;
  padding: 3px;
  border-radius: 0;
  background: rgba(118, 118, 128, 0.12);
  overflow: hidden;
}
.sp-projection {
  min-width: 34px;
  padding: 4px 8px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #9a9a9a;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.08em;
  line-height: 1;
  cursor: pointer;
  transition: color 160ms ease, background-color 180ms ease;
}
.sp-projection:hover {
  color: #333;
  background: rgba(255, 255, 255, 0.45);
}
.sp-projection.active {
  color: #333;
  background: rgba(255, 255, 255, 0.9);
}
.sp-results {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 6px 0;
  container-type: inline-size;
}
.sp-panel-map .sp-results {
  display: flex;
  overflow: hidden;
  padding: 0;
}
.sp-map {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  padding: 8px 0 0;
}
.sp-map :deep(.emap) {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}
.sp-map :deep(.emap-stage) {
  flex: 1 1 auto;
  min-height: 0;
}
.sp-group { padding: 4px 0 7px; }
.sp-group-label {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.62rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #aaaaaa;
  padding: 8px 20px 5px;
}
.sp-result {
  display: block;
  width: calc(100% - 16px);
  margin: 2px 8px;
  text-align: left;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 9px 12px;
  border-radius: 0;
  transition: background-color 120ms ease, transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-result.active {
  background: rgba(118, 118, 128, 0.13);
}
.sp-result:active { transform: scale(0.992); }
/* Jump-to rows: a soft wash, the title nudging forward, and an arrow that
   fades in from the right when the destination is reachable. */
.sp-jump {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  transition: background-color 220ms ease, transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-jump .sp-result-title {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
}
.sp-jump-arrow {
  flex: none;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.92rem;
  line-height: 1;
  color: #686868;
  opacity: 0;
  transform: translateX(8px);
  transition: opacity 220ms ease, transform 300ms cubic-bezier(0.22, 1, 0.36, 1);
}
.sp-jump:hover,
.sp-jump:focus-visible,
.sp-jump.active {
  background: rgba(118, 118, 128, 0.08);
}
.sp-jump:hover .sp-jump-arrow,
.sp-jump:focus-visible .sp-jump-arrow,
.sp-jump.active .sp-jump-arrow {
  opacity: 1;
  transform: translateX(0);
}
.sp-jump:hover .sp-result-title,
.sp-jump:focus-visible .sp-result-title,
.sp-jump.active .sp-result-title {
  transform: translateX(3px);
}
body.dark-mode .sp-jump-arrow {
  color: #b6b6b6;
}
.sp-result-title {
  display: block;
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.96rem;
  color: #1f1f1f;
  line-height: 1.35;
}
.sp-result-snippet {
  display: block;
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.8rem;
  color: #777;
  line-height: 1.4;
  margin-top: 2px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.sp-result :deep(mark) {
  background: rgba(255, 222, 120, 0.45);
  color: inherit;
  border-radius: 2px;
  padding: 0 1px;
}
.sp-empty {
  font-family: "PT Serif", Georgia, serif;
  color: #999;
  padding: 22px 18px;
  font-size: 0.92rem;
}
.sp-view-compose {
  padding: 6px 0;
}
.sp-view-compose .compose-progress {
  margin: 0 14px;
}
.sp-view-start {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 14px 14px 16px;
}
.sp-view-note {
  position: relative;
  max-width: 31rem;
  margin: 0 6px 14px;
  padding-left: 12px;
  color: #77777d;
  font: 0.78rem/1.48 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
}
.sp-view-note::before {
  content: "";
  position: absolute;
  top: 0.18em;
  bottom: 0.18em;
  left: 0;
  width: 2px;
  border-radius: 2px;
  background: rgba(var(--site-accent-rgb), 0.34);
}
.sp-view-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 0 6px 8px;
}
.sp-view-public-link {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  min-height: 28px;
  padding: 0 0.2rem;
  border: 0;
  background: transparent;
  color: #77777d;
  font: 600 0.7rem/1.2 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  cursor: pointer;
  white-space: nowrap;
  transition: color 160ms ease, transform 100ms ease-out;
}
.sp-view-public-link > .site-arrow-up-right {
  color: currentColor;
  transition: transform 220ms cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-view-public-link:hover,
.sp-view-public-link:focus-visible {
  color: #28282b;
  outline: none;
}
.sp-view-public-link:hover > .site-arrow-up-right,
.sp-view-public-link:focus-visible > .site-arrow-up-right { transform: translate(2px, -2px); }
.sp-view-public-link:active { transform: scale(0.98); }
.sp-view-examples-label {
  color: #9a9a9f;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.sp-view-examples {
  display: grid;
  overflow: hidden;
  border: 1px solid rgba(60, 60, 67, 0.1);
  border-radius: 12px;
  background: rgba(118, 118, 128, 0.055);
}
.sp-view-examples button,
.sp-view-skeleton-row {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 46px;
  padding: 0.7rem 0.85rem;
  border: 0;
  border-bottom: 1px solid rgba(60, 60, 67, 0.08);
}
.sp-view-examples button {
  appearance: none;
  background: transparent;
  color: #515155;
  font: 0.78rem/1.35 -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
  text-align: left;
  cursor: pointer;
  transition: color 160ms ease, background-color 160ms ease, transform 100ms ease-out;
}
.sp-view-examples button:last-child,
.sp-view-skeleton-row:last-child { border-bottom: 0; }
.sp-view-examples button:hover,
.sp-view-examples button:focus-visible {
  color: #202023;
  background: rgba(118, 118, 128, 0.09);
  outline: none;
}
.sp-view-examples button:active {
  transform: scale(0.98);
}
.sp-view-examples button:disabled {
  cursor: wait;
  opacity: 0.52;
}
.sp-view-example-copy,
.sp-view-skeleton-copy {
  display: grid;
  grid-template-rows: calc(0.78rem * 1.35) calc(0.68rem * 1.35);
  gap: 0.18rem;
  min-width: 0;
}
.sp-view-example-copy strong,
.sp-view-example-copy small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sp-view-example-copy strong {
  color: inherit;
  font-size: 0.78rem;
  font-weight: 600;
}
.sp-view-example-copy small {
  color: #8b8b91;
  font-size: 0.68rem;
}
.sp-view-example-arrow {
  flex: 0 0 auto;
  color: #9a9a9f;
  transition: transform 180ms cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-view-examples button:hover .sp-view-example-arrow,
.sp-view-examples button:focus-visible .sp-view-example-arrow { transform: translateX(2px); }
.sp-view-examples-loading {
  pointer-events: none;
}
.sp-view-skeleton-copy {
  align-items: center;
  width: min(78%, 20rem);
}
.sp-view-skeleton-line,
.sp-view-skeleton-mark {
  display: block;
  border-radius: 999px;
  background: linear-gradient(
    100deg,
    rgba(118, 118, 128, 0.09) 18%,
    rgba(118, 118, 128, 0.17) 42%,
    rgba(118, 118, 128, 0.09) 66%
  );
  background-size: 240% 100%;
  animation: sp-view-skeleton-shift 1.5s ease-in-out infinite;
}
.sp-view-skeleton-title {
  width: 62%;
  height: 0.62rem;
}
.sp-view-skeleton-question {
  width: 88%;
  height: 0.48rem;
}
.sp-view-skeleton-mark {
  width: 0.78rem;
  height: 0.48rem;
}
@keyframes sp-view-skeleton-shift {
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
}
body.dark-mode .sp-view-note { color: #a5a5aa; }
body.dark-mode .sp-view-public-link { color: #a5a5aa; }
body.dark-mode .sp-view-public-link:hover,
body.dark-mode .sp-view-public-link:focus-visible { color: #f2f2f5; }
body.dark-mode .sp-view-examples {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(118, 118, 128, 0.1);
}
body.dark-mode .sp-view-examples button {
  color: #d9d9dd;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
body.dark-mode .sp-view-example-copy small { color: #a3a3a8; }
body.dark-mode .sp-view-examples button:hover,
body.dark-mode .sp-view-examples button:focus-visible {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.07);
}
body.dark-mode .sp-view-skeleton-row {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}
body.dark-mode .sp-view-skeleton-line,
body.dark-mode .sp-view-skeleton-mark {
  background-image: linear-gradient(
    100deg,
    rgba(255, 255, 255, 0.07) 18%,
    rgba(255, 255, 255, 0.14) 42%,
    rgba(255, 255, 255, 0.07) 66%
  );
}
.sp-ask {
  padding: 6px 0;
}
.sp-ask > :deep(.ask-audio) {
  width: calc(100% - 36px);
  /* The answer already carries 14px of bottom padding. Keep the audio
     centred between that text and the following actions. */
  margin: 0 18px .85rem;
}
.sp-ask-empty {
  text-align: left;
}
.sp-ask-sample {
  appearance: none;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  line-height: inherit;
  padding: 0;
  max-width: none;
  text-align: left;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 260ms ease, transform 260ms ease, color 160ms ease;
}
.sp-ask-sample.visible {
  opacity: 1;
  transform: translateY(0);
}
.sp-ask-sample:hover,
.sp-ask-sample:focus-visible {
  color: #222;
  outline: none;
}
:global(body.dark-mode .sp-ask-sample:hover),
:global(body.dark-mode .sp-ask-sample:focus-visible) {
  color: #fff;
}
@media (prefers-reduced-motion: reduce) {
  .sp-view-skeleton-line,
  .sp-view-skeleton-mark {
    animation: none;
    background-position: 50% 0;
  }
  .sp-view-examples button,
  .sp-view-public-link,
  .sp-view-public-link > .site-arrow-up-right {
    transition: none;
  }
  .sp-ask-sample {
    opacity: 1;
    transform: none;
    transition: color 160ms ease;
  }
}
.sp-ask-answer {
  font-family: "PT Serif", Georgia, serif;
  font-size: 0.96rem;
  line-height: 1.6;
  color: #333;
  white-space: pre-wrap;
  margin: 0;
  padding: 14px 18px;
}

.sp-ask-answer a.ask-inline-link {
  color: var(--site-accent);
  text-decoration: underline;
  text-decoration-color: rgba(var(--site-accent-rgb), 0.45);
  text-underline-offset: 2px;
  transition: color 140ms ease, text-decoration-color 140ms ease;
}

.sp-ask-answer a.ask-inline-link:hover,
.sp-ask-answer a.ask-inline-link:focus-visible {
  color: var(--site-accent-strong);
  text-decoration-color: currentColor;
}
.sp-ask-sources {
  padding-top: 4px;
}
.sp-ask-source-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  padding: 2px 18px 6px;
}

.sp-ask-cite {
  color: #aaa;
  margin-right: 4px;
}
body.dark-mode .sp-ask-answer { color: #e4e4e4; }
body.dark-mode .sp-ask-answer a.ask-inline-link {
  color: var(--site-accent-strong);
  text-decoration-color: rgba(var(--site-accent-rgb), 0.55);
}
body.dark-mode .sp-ask-answer a.ask-inline-link:hover,
body.dark-mode .sp-ask-answer a.ask-inline-link:focus-visible {
  color: var(--site-accent-hover);
}

body.dark-mode .sp-ask-cite { color: var(--site-text-tertiary); }


.sp-ask-preview {
  position: absolute;
  z-index: 30;
  box-sizing: border-box;
  padding: 9px 12px;
  border: 1px solid rgba(60, 60, 67, 0.14);
  border-radius: 8px;
  background: rgba(250, 250, 251, 0.96);
  backdrop-filter: blur(10px) saturate(140%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  pointer-events: none;
}
.sp-ask-preview-kicker {
  display: block;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.6rem;
  line-height: 1.15;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sp-ask-preview-text {
  margin: 3px 0 0;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #555;
}
.sp-preview-fade-enter-active,
.sp-preview-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.2s cubic-bezier(0.23, 1, 0.32, 1);
}
.sp-preview-fade-enter-from,
.sp-preview-fade-leave-to {
  opacity: 0;
  transform: translateY(-3px);
}
body.dark-mode .sp-ask-preview {
  background: rgba(44, 44, 46, 0.96);
  border-color: rgba(255, 255, 255, 0.12);
}
body.dark-mode .sp-ask-preview-kicker { color: var(--site-text-tertiary); }
body.dark-mode .sp-ask-preview-text { color: var(--site-text-secondary); }
.sp-ask-followups {
  padding: 2px 0 6px;
}
.sp-ask-view-offer { margin: 4px 18px 14px; }
.sp-view-error { color: #8a4d45; }
.sp-ask-followup-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.42rem;
  padding: 2px 18px 8px;
}




.sp-foot {
  display: flex;
  gap: 16px;
  padding: 10px 16px 11px;
  border-top: 1px solid rgba(60, 60, 67, 0.1);
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.66rem;
  color: #9a9a9a;
}
.sp-foot kbd {
  font-family: inherit;
  border: 1px solid rgba(60, 60, 67, 0.14);
  border-radius: 2px;
  padding: 1px 5px;
  margin-right: 2px;
  color: #888;
}
:global(html.theme-transitioning .sp-mode),
:global(body.theme-transitioning .sp-mode),
:global(html.theme-transitioning .sp-projection),
:global(body.theme-transitioning .sp-projection),
:global(html.theme-transitioning .sp-projection-toggle),
:global(body.theme-transitioning .sp-projection-toggle),
:global(html.theme-transitioning .sp-input-row),
:global(body.theme-transitioning .sp-input-row),
:global(html.theme-transitioning .sp-esc),
:global(body.theme-transitioning .sp-esc),
:global(html.theme-transitioning .sp-foot),
:global(body.theme-transitioning .sp-foot),
:global(html.theme-transitioning .sp-foot kbd),
:global(body.theme-transitioning .sp-foot kbd) {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease,
    border-color var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .sp-ask-sample),
:global(body.theme-transitioning .sp-ask-sample) {
  transition:
    opacity 260ms ease,
    transform 260ms ease,
    color var(--theme-duration, 220ms) ease;
}
:global(html.theme-transitioning .sp-ask-answer),
:global(body.theme-transitioning .sp-ask-answer),
:global(html.theme-transitioning .sp-ask-source),
:global(body.theme-transitioning .sp-ask-source),
:global(html.theme-transitioning .sp-ask-cite),
:global(body.theme-transitioning .sp-ask-cite) {
  transition:
    color var(--theme-duration, 220ms) ease,
    background-color var(--theme-duration, 220ms) ease;
}
.sp-overlay { animation: sp-overlay-in 0.18s ease; }
.sp-panel { animation: sp-panel-in 0.18s cubic-bezier(0.23, 1, 0.32, 1); }
.sp-overlay.sp-instant,
.sp-instant .sp-panel {
  animation: none;
}
.sp-overlay.sp-departing {
  opacity: 0;
  animation: none;
  transition: opacity 105ms ease-in;
  pointer-events: none;
}
.sp-departing .sp-panel {
  opacity: 0;
  transform: translateY(-4px) scale(0.997);
  animation: none;
  transition: opacity 90ms ease-in, transform 105ms ease-in;
}
@keyframes sp-overlay-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes sp-panel-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
@media (max-width: 1100px) {
  .sp-overlay {
    padding: max(7vh, calc(env(safe-area-inset-top) + 12px)) 10px 10px;
    background: rgba(24, 24, 28, 0.22);
    backdrop-filter: blur(8px) saturate(112%);
    -webkit-backdrop-filter: blur(8px) saturate(112%);
  }
  /* Embedded palettes (Search / Ask rendered inside a page) stay in the page
     flow on phones too, so the modal backdrop must not reappear. */
  .sp-overlay.sp-embedded {
    padding: 0;
    background: transparent;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  .sp-panel {
    height: min(82dvh, 620px);
    max-height: min(82dvh, 620px);
    border-radius: 16px;
    box-shadow: 0 18px 46px rgba(0, 0, 0, 0.16);
    overflow: hidden;
  }
  /* Embedded palettes (Search / Ask inside a page) keep their natural height
     on phones and drop the floating-panel shadow. */
  .sp-embedded .sp-panel {
    height: auto;
    max-height: none;
    border-radius: 0;
    box-shadow: none;
  }
  .sp-panel-map {
    height: min(82dvh, 620px);
  }
  .sp-input-row {
    gap: 9px;
    min-height: 60px;
    padding: 10px 12px 9px 15px;
  }
  .sp-input-icon {
    font-size: 0.98rem;
  }
  .sp-input {
    min-height: 40px;
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
    font-size: clamp(1.12rem, 3vw, 1.35rem);
  }
  .sp-clear {
    flex-basis: 32px;
    width: 32px;
    height: 32px;
  }
  .sp-esc {
    display: grid;
    place-items: center;
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: rgba(118, 118, 128, 0.12);
    color: #68686d;
    font-size: 0.82rem;
    letter-spacing: 0;
  }
  .sp-esc:hover {
    color: #343438;
    border-color: transparent;
    background: rgba(118, 118, 128, 0.18);
  }
  .sp-esc:active {
    background: rgba(118, 118, 128, 0.22);
    transform: scale(0.92);
  }
  .sp-close-desktop {
    display: none;
  }
  .sp-close-mobile {
    display: block;
    width: 18px;
    height: 18px;
  }
  .sp-modes {
    position: relative;
    z-index: 20;
    gap: 7px;
    min-height: 48px;
    padding: 5px 12px 6px;
  }
  .sp-mode-segments {
    display: none;
  }
  .sp-mobile-mode-trigger {
    appearance: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 96px;
    min-height: 38px;
    padding: 0 11px;
    border: 0;
    border-radius: 11px;
    background: rgba(118, 118, 128, 0.1);
    color: #343438;
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: color 160ms ease, background-color 160ms ease, transform 120ms cubic-bezier(0.23, 1, 0.32, 1);
  }
  .sp-mobile-mode-trigger > i {
    margin-left: auto;
    color: #8e8e93;
    font-size: 0.62rem;
    transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .sp-mobile-mode-trigger.open > i {
    transform: rotate(180deg);
  }
  .sp-mobile-mode-trigger:active {
    transform: scale(0.97);
  }
  .sp-mobile-mode-current {
    display: inline-flex;
    align-items: baseline;
  }
  .sp-mobile-mode-current :deep(.ai-ask-term),
  .sp-mobile-mode-current :deep(.ai-view-term) {
    font-family: inherit;
    font-weight: inherit;
  }
  .sp-mobile-mode-menu {
    position: absolute;
    top: calc(100% + 5px);
    left: 12px;
    right: 12px;
    z-index: 30;
    display: grid;
    gap: 2px;
    padding: 6px;
    border: 1px solid rgba(60, 60, 67, 0.12);
    border-radius: 14px;
    background: rgba(250, 250, 251, 0.98);
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.17);
    backdrop-filter: blur(18px) saturate(150%);
    -webkit-backdrop-filter: blur(18px) saturate(150%);
  }
  .sp-mobile-mode-option {
    appearance: none;
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) 10px;
    align-items: center;
    gap: 9px;
    width: 100%;
    min-height: 54px;
    padding: 7px 10px;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #303034;
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .sp-mobile-mode-option:active {
    background: rgba(118, 118, 128, 0.1);
  }
  .sp-mobile-mode-trigger:disabled,
  .sp-mobile-mode-option:disabled {
    cursor: default;
    opacity: 0.46;
  }
  .sp-mobile-mode-option > i {
    color: #77777d;
    font-size: 0.98rem;
    text-align: center;
  }
  .sp-mobile-mode-option > span:nth-child(2) {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .sp-mobile-mode-option strong {
    color: inherit;
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
    font-size: 0.84rem;
    font-weight: 600;
    line-height: 1.15;
  }
  .sp-mobile-mode-option small {
    color: #85858a;
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
    font-size: 0.7rem;
    line-height: 1.2;
  }
  .sp-mobile-mode-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: transparent;
  }
  .sp-mobile-mode-option.active {
    background: rgba(118, 118, 128, 0.16);
    color: #171719;
  }
  .sp-mobile-mode-option.active .sp-mobile-mode-dot {
    background: #3a3a3c;
  }
  .sp-mode-status {
    margin-left: auto;
  }
  .sp-projection-toggle {
    margin-left: auto;
    padding: 3px;
    border-radius: 10px;
  }
  .sp-projection {
    min-width: 43px;
    min-height: 34px;
    padding: 0 8px;
    border-radius: 8px;
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif;
    font-size: 0.74rem;
    font-weight: 600;
    letter-spacing: 0;
  }
  .sp-projection.active {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  .sp-results {
    display: flex;
    flex-direction: column;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
  .sp-view-compose,
  .sp-ask {
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
  }
  .sp-view-start {
    justify-content: center;
    min-height: 100%;
    box-sizing: border-box;
    padding: 18px 14px 42px;
  }
  .sp-ask-empty {
    display: grid;
    flex: 1 0 auto;
    place-items: center;
    box-sizing: border-box;
    min-height: 100%;
    text-align: center;
  }
  .sp-result {
    min-height: 48px;
    padding-block: 11px;
  }
  .sp-ask-preview {
    display: none;
  }
  .sp-foot {
    display: none;
  }
  :global(body.dark-mode .sp-esc) {
    color: #c7c7cc;
    background: rgba(118, 118, 128, 0.2);
  }
  :global(body.dark-mode .sp-mobile-mode-trigger) {
    color: #ededf0;
    background: rgba(118, 118, 128, 0.2);
  }
  :global(body.dark-mode .sp-mobile-mode-menu) {
    border-color: rgba(255, 255, 255, 0.1);
    background: rgba(44, 44, 46, 0.98);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.38);
  }
  :global(body.dark-mode .sp-mobile-mode-option) {
    color: #ececef;
  }
  :global(body.dark-mode .sp-mobile-mode-option > i),
  :global(body.dark-mode .sp-mobile-mode-option small) {
    color: #a1a1a6;
  }
  :global(body.dark-mode .sp-mobile-mode-option.active) {
    color: #f5f5f7;
    background: rgba(118, 118, 128, 0.34);
  }
  :global(body.dark-mode .sp-mobile-mode-option.active .sp-mobile-mode-dot) {
    background: #e5e5e8;
  }
  .sp-mobile-mode-menu-enter-active,
  .sp-mobile-mode-menu-leave-active {
    transition: opacity 160ms ease, transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .sp-mobile-mode-menu-enter-from,
  .sp-mobile-mode-menu-leave-to {
    opacity: 0;
    transform: translateY(-5px) scale(0.985);
  }

  /* On phones Search is part of the global navigation surface. The surface
     unfolds from the header edge and gives every mode the whole viewport. */
  .sp-overlay:not(.sp-embedded) {
    --sp-mobile-content-inset: clamp(3rem, 8vw, 6rem);
    display: block;
    padding: 0;
    overflow: hidden;
    background: #fafafc;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    animation: sp-mobile-surface-in 320ms cubic-bezier(0.4, 0, 0.6, 1) both;
    transform-origin: top center;
  }
  :global(body.dark-mode .sp-overlay:not(.sp-embedded)) {
    background: #161617;
  }
  .sp-overlay:not(.sp-embedded) .sp-panel {
    width: 100%;
    max-width: none;
    height: 100dvh;
    max-height: 100dvh;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    animation: none;
  }
  .sp-overlay:not(.sp-embedded) .sp-panel-map {
    height: 100dvh;
    max-height: 100dvh;
    max-width: none;
  }
  .sp-overlay:not(.sp-embedded) .sp-input-row {
    position: relative;
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 12px;
    min-height: calc(118px + env(safe-area-inset-top));
    padding:
      calc(54px + env(safe-area-inset-top))
      var(--sp-mobile-content-inset)
      16px;
    border-bottom: 0;
  }
  .sp-overlay:not(.sp-embedded) .sp-esc {
    position: absolute;
    top: calc(14px + env(safe-area-inset-top));
    right: 5vw;
    width: 36px;
    height: 36px;
    background: transparent;
    color: #3a3a3c;
    font-size: 0.95rem;
  }
  @media (min-width: 761px) and (max-width: 1100px) {
    .sp-overlay:not(.sp-embedded) {
      --sp-mobile-content-inset: clamp(5rem, 12vw, 9rem);
    }
    .sp-overlay:not(.sp-embedded) .sp-input-row {
      min-height: calc(142px + env(safe-area-inset-top));
      padding-top: calc(78px + env(safe-area-inset-top));
    }
    .sp-overlay:not(.sp-embedded) .sp-esc {
      top: calc(11px + env(safe-area-inset-top));
      right: 5vw;
      width: 46px;
      height: 56px;
    }
    .sp-overlay:not(.sp-embedded) .sp-close-mobile {
      transform: translateX(14px) scale(1.2);
    }
  }
  .sp-overlay:not(.sp-embedded) .sp-esc:hover,
  .sp-overlay:not(.sp-embedded) .sp-esc:active {
    background: transparent;
    transform: none;
  }
  :global(body.dark-mode .sp-overlay:not(.sp-embedded) .sp-esc) {
    color: #f5f5f7;
    background: transparent;
  }
  .sp-overlay:not(.sp-embedded) .sp-results {
    flex: 1 1 auto;
    width: calc(100% - 2 * var(--sp-mobile-content-inset));
    min-height: 0;
    max-height: none;
    margin-inline: auto;
  }
  .sp-overlay:not(.sp-embedded) .sp-result {
    border-radius: 8px;
  }
  .sp-overlay:not(.sp-embedded) .sp-panel-map .sp-results {
    padding-block: 14px;
  }
  .sp-overlay:not(.sp-embedded) .sp-map {
    padding-block: 0;
  }
  .sp-overlay:not(.sp-embedded) .sp-map :deep(.emap-stage) {
    box-sizing: border-box;
    border-radius: 12px;
  }
  .sp-overlay:not(.sp-embedded) .sp-modes {
    box-sizing: border-box;
    width: calc(100% - 2 * var(--sp-mobile-content-inset));
    margin-inline: auto;
    padding: 9px 0 5px;
  }
  .sp-overlay:not(.sp-embedded) .sp-view-start {
    justify-content: flex-start;
    min-height: auto;
    padding: 24px 0 44px;
  }
  .sp-overlay:not(.sp-embedded) .sp-view-note {
    margin: 0 0 22px;
  }
  .sp-overlay:not(.sp-embedded) .sp-view-heading-row {
    padding-inline: 0;
  }
  .sp-overlay:not(.sp-embedded) .sp-ask-empty {
    display: block;
    min-height: auto;
    padding: 26px 0 44px;
    text-align: left;
  }
  .sp-overlay:not(.sp-embedded) .sp-ask-sample {
    max-width: 31rem;
    color: #515156;
    font-size: clamp(1rem, 2.4vw, 1.18rem);
    line-height: 1.45;
  }
  :global(body.dark-mode .sp-overlay:not(.sp-embedded) .sp-ask-sample) {
    color: #c7c7cc;
  }
  .sp-overlay:not(.sp-embedded) .sp-input-row,
  .sp-overlay:not(.sp-embedded) .sp-modes,
  .sp-overlay:not(.sp-embedded) .sp-results {
    animation: sp-mobile-content-in 280ms cubic-bezier(0.4, 0, 0.6, 1) backwards;
  }
  .sp-overlay:not(.sp-embedded) .sp-modes {
    animation-delay: 40ms;
  }
  .sp-overlay:not(.sp-embedded) .sp-results {
    animation-delay: 70ms;
  }
  .sp-overlay.sp-mobile-closing:not(.sp-embedded) {
    pointer-events: none;
    animation: sp-mobile-surface-out 320ms cubic-bezier(0.32, 0.72, 0, 1) both;
  }
  .sp-overlay.sp-mobile-closing:not(.sp-embedded) .sp-input-row,
  .sp-overlay.sp-mobile-closing:not(.sp-embedded) .sp-modes,
  .sp-overlay.sp-mobile-closing:not(.sp-embedded) .sp-results {
    opacity: 0;
    transform: translateY(-8px);
    animation: none;
    transition: opacity 180ms cubic-bezier(0.4, 0, 0.6, 1), transform 180ms cubic-bezier(0.4, 0, 0.6, 1);
  }
  .sp-overlay.sp-mobile-closing:not(.sp-embedded) .sp-modes {
    transition-delay: 18ms;
  }
  .sp-overlay.sp-mobile-closing:not(.sp-embedded) .sp-input-row {
    transition-delay: 36ms;
  }
}
/* Hover-capable pointers on the compact layout get a subtle row highlight in
   the site's black-and-white palette, following the desktop mode segments:
   hover tints, press deepens, and the selected row keeps its accent text so
   its state stays legible. Scoped to (hover: hover) so touch devices never
   pin a stale hover state. */
@media (hover: hover) and (max-width: 1100px) {
  .sp-mobile-mode-option,
  .sp-mobile-mode-option > i,
  .sp-mobile-mode-option small {
    transition: background-color 130ms ease, color 130ms ease;
  }
  .sp-mobile-mode-trigger:not(:disabled):hover {
    background: rgba(118, 118, 128, 0.16);
  }
  .sp-mobile-mode-option:not(:disabled):hover {
    background: rgba(118, 118, 128, 0.08);
    color: #171719;
  }
  .sp-mobile-mode-option:not(:disabled):hover > i,
  .sp-mobile-mode-option:not(:disabled):hover small {
    color: #6b6b70;
  }
  .sp-mobile-mode-option:not(:disabled):active {
    background: rgba(118, 118, 128, 0.13);
  }
  .sp-mobile-mode-option.active:not(:disabled):hover,
  .sp-mobile-mode-option.active:not(:disabled):active {
    background: rgba(118, 118, 128, 0.2);
  }
  :global(body.dark-mode .sp-mobile-mode-trigger:not(:disabled):hover) {
    background: rgba(118, 118, 128, 0.3);
  }
  :global(body.dark-mode .sp-mobile-mode-option:not(:disabled):hover) {
    background: rgba(118, 118, 128, 0.18);
    color: #f5f5f7;
  }
  :global(body.dark-mode .sp-mobile-mode-option:not(:disabled):hover > i),
  :global(body.dark-mode .sp-mobile-mode-option:not(:disabled):hover small) {
    color: #d1d1d6;
  }
  :global(body.dark-mode .sp-mobile-mode-option:not(:disabled):active) {
    background: rgba(118, 118, 128, 0.28);
  }
  :global(body.dark-mode .sp-mobile-mode-option.active:not(:disabled):hover),
  :global(body.dark-mode .sp-mobile-mode-option.active:not(:disabled):active) {
    background: rgba(118, 118, 128, 0.4);
  }
}
@keyframes sp-mobile-surface-in {
  from { clip-path: inset(0 0 100% 0); transform: translateY(-10px); }
  to { clip-path: inset(0); transform: translateY(0); }
}
@keyframes sp-mobile-surface-out {
  from { clip-path: inset(0); transform: translateY(0); }
  to { clip-path: inset(0 0 100% 0); transform: translateY(-10px); }
}
@keyframes sp-mobile-content-in {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .sp-overlay,
  .sp-panel {
    animation: none;
  }
  .sp-mobile-mode-trigger > i,
  .sp-mobile-mode-menu-enter-active,
  .sp-mobile-mode-menu-leave-active {
    transition: none;
  }
  .sp-overlay:not(.sp-embedded),
  .sp-overlay:not(.sp-embedded) .sp-input-row,
  .sp-overlay:not(.sp-embedded) .sp-modes,
  .sp-overlay:not(.sp-embedded) .sp-results,
  .sp-overlay.sp-mobile-closing:not(.sp-embedded) {
    animation: none;
    transition: none;
  }
}
</style>
